import type {
    DataFieldAbstractTypes,
    DataFieldForActionAbstractTypes,
    LineItem
} from '@sap-ux/vocabularies-types/vocabularies/UI';
import { UIAnnotationTerms, UIAnnotationTypes } from '@sap-ux/vocabularies-types/vocabularies/UI';
import type { AnnotationTerm, ConvertedMetadata, EntitySet, EntityType } from '@sap-ux/vocabularies-types';
import {
    addManifestPathsToProperties as addManifestPathsToPropertiesCommon,
    convertSyncRulePathToJsonPath,
    DEFINITION_LINK_PREFIX,
    findAlias,
    getDefinitionKey,
    getJSONPropertyByPath,
    type ManifestPathParams
} from '../../common/utils';
import { getStableIdPartFromDataField } from './StableIdHelper';
import {
    addCommonLineItemDefinitions,
    addDescriptiveEnumDefinition,
    addEnumToSchema,
    addKeyToDefinition,
    compareTemplateNames,
    type DataFieldStrategyTypes,
    determineDataType,
    getActionKey,
    getDataFieldDescription,
    getEnumEntry,
    getFacetDefinitionKey,
    getFacetDefinitionLink,
    getReflectMetadata,
    getUniqueFacetDefinitionLink,
    parseSchemaDefinition,
    prepareRef,
    removeEmptyStructure
} from '../../common';
import i18next from 'i18next';
import { log, LogSeverity } from '../../..';
import type { Definition, DefinitionOrBoolean } from 'typescript-json-schema';
import {
    ActionType,
    CustomUIAnnotationTypes,
    DataFieldActionType,
    DefinitionName,
    type ExtensionLogger,
    type FileData,
    type Manifest,
    MANIFESTPATH,
    ManifestSection,
    type OneOfOption,
    PageTypeV4,
    PropertyName,
    type SchemaDefinition,
    type SchemaDefinitionKey,
    SchemaKeyName,
    SchemaTag,
    TemplatePropertyName,
    TemplateType,
    UIVOCABULARY,
    v4
} from '@sap/ux-specification-types';
import { join } from 'path';
import { type ExtensionFileData } from '../types';
import { dataFieldStrategyContext } from '../../common/DataFieldStrategy';
import type { MetadataInstanceInterface } from '../../common/decoration/factory';
import { importSettingsOfObject } from '../import/utils';
import { type CreationFieldOneOfOption, CreationFieldType } from '@sap/ux-specification-types/src';
import type { EntityTypeAnnotations_UI } from '@sap-ux/vocabularies-types/vocabularies/UI_Edm';
import * as v4controls from '../export/controls';
import * as pages from '../export/pages';
import { getPathForPage } from '../application';

export type CustomExtension =
    | v4.TableCustomColumn
    | v4.CustomAction
    | v4.CustomActionMenu
    | v4.SapUi5RoutingTargetContentSection
    | v4.SapUiGenericAppPageCustomFilterField;

export interface CustomExtensions {
    [key: string]: CustomExtension;
}

/**
 * Parameter object used while generating/appending LineItem entries (columns and actions) into the app schema.
 *
 * This bundle is passed into helpers that write schema fragments for different record types
 * (e.g. DataField columns, single actions, and action groups). It provides:
 * - the current LineItem context (entity type, schema, annotation path),
 * - the target schema node (actions definition / line item definition),
 * - the concrete record and its index in the annotation collection.
 *
 * @property lineItemData Context describing where the LineItem is written (app schema, entity type, annotation, etc.).
 * @property actions Target schema definition that collects action entries (toolbar/actions container).
 * @property lineItemPath Annotation path pointing to the LineItem collection being processed.
 * @property lineItemRecord The concrete LineItem record currently processed (may represent an action or a field-like record).
 * @property index Index of `lineItemRecord` within the LineItem annotation array; used for ordering and annotationPath.
 * @property lineItemDefinition Optional schema definition to write into when generating inline/embedded objects (no $ref case).
 * @property columnDefinitionName Base definition name used for column records (may be specialized for action columns).
 */
export interface AddLineItemDefinitionParams {
    lineItemData: LineItemDataForSchema;
    actions: Definition;
    lineItemPath: string;
    lineItemRecord: DataFieldForActionAbstractTypes;
    index: number;
    lineItemDefinition?: Definition;
    columnDefinitionName: string;
}

/**
 * Parameter object used when importing action settings from a schema definition reference into the generated config.
 *
 * This bundle is passed through the different action-import helpers/strategies (single action, group action,
 * manifest-based action menu) and carries:
 * - the schema fragments needed to resolve/read properties,
 * - the target config object where settings are written,
 * - manifest routing/context information required to compute correct manifest paths.
 *
 * @property actionPropertyDefinition Schema definition describing the action's properties (source of truth for what can be imported).
 * @property appSchema Application-specific JSON schema containing all definitions and references.
 * @property factory Metadata factory used to resolve sync rules / decorators and to materialize config instances.
 * @property actionsInConfig Target object in the config that receives imported action settings (e.g. actions map under a section/table).
 * @property actionKey Key/name of the action within `actionsInConfig` (the entry to read/write).
 * @property manifest The application manifest; used to resolve manifest paths for the imported settings.
 * @property routingId Routing target/page identifier providing context for manifest path resolution.
 * @property manifestSectionId Optional section identifier in the manifest (e.g. OP section id) to scope the action settings.
 * @property targetAnnotation Optional annotation path/identifier that further scopes action settings (e.g. LineItem qualifier / view context).
 */
export interface ImportActionSettingsOfReferenceParams {
    actionPropertyDefinition: Definition;
    appSchema: Definition;
    factory: MetadataInstanceInterface;
    actionsInConfig: object;
    actionKey: string;
    manifest: Manifest;
    routingId: string;
    manifestSectionId?: string;
    targetAnnotation?: string;
}

/**
 * Internal parameter object used while merging custom actions / action menus into a schema definition.
 *
 * The action processing logic takes an initial list of schema action entries, enriches it with manifest extensions,
 * and writes the final, ordered result back into the schema.
 *
 * @property appSchema Application-specific JSON schema containing all definitions and references used during processing.
 * @property extensions Manifest-defined custom actions / action menu entries to be merged into the schema.
 * @property initialActionDetailsList Current (already sorted/collected) list of action entries from the schema definition.
 * @property customActionDefinitionName Definition name used for custom action references (varies by page/context).
 * @property logger Logger used to report invalid extensions, anchors, or other inconsistencies.
 * @property lineItemId Optional LineItem identifier (e.g. table/view context) used to scope/derive action definition names.
 */
interface ActionProcessingParameters {
    appSchema: Definition;
    extensions: CustomExtensions;
    initialActionDetailsList: ActionDetails[];
    customActionDefinitionName: string;
    logger: ExtensionLogger;
    lineItemId?: string;
}

/**
 * Pair of definition names used for action menus:
 * - the menu container definition,
 * - the definition that holds the menu's actions/entries.
 *
 * @property actionMenuDefinitionName Definition name for the action menu container (the menu object itself).
 * @property actionMenuActionsDefinitionName Definition name for the collection of menu actions/items.
 * @property actionName Definition name for the action type (used for menu entries).
 */
interface ActionMenuDefinitionName {
    actionMenuDefinitionName: DefinitionName;
    actionMenuActionsDefinitionName: DefinitionName;
    actionName: DefinitionName;
}

/**
 * Parsed schema definition identifier split into base name and optional qualifier.
 *
 * Used when a schema definition name encodes additional context (e.g. qualifier, facet/lineItem id) and callers
 * need to distinguish the generic base definition from the specific qualified variant.
 *
 * @property baseDefinitionName The unqualified/base schema definition name.
 * @property definitionQualifier Optional qualifier extracted from the full definition name (format depends on naming convention).
 */
export interface ParsedDefinitionName {
    baseDefinitionName: DefinitionName;
    definitionQualifier?: string;
}

export type ActionDetails = [string, DefinitionOrBoolean];

export const DATA_FIELD_FOR_ACTION_GROUP = 'DataFieldForActionGroup';
const UI_ANNOTATION_KEY = 'UI';
export const FIELD_SEPARATOR = '::';
export const QUALIFIER_SEPARATOR = '#';

/**
 * List of annotation-based action prefixes that should be included in the schema
 * even if they lack custom action properties (press, menu), so they can be validated
 * and removed during export if they don't match the config.
 */
const ANNOTATION_BASED_ACTION_PREFIXES = [`${DataFieldActionType.DataFieldForIntentBasedNavigation}${FIELD_SEPARATOR}`];

const ACTION_TYPE = 'actionType';
const strategyTypeMap = new Map<string, DataFieldStrategyTypes>([
    ['', UIAnnotationTypes.DataFieldForAction],
    ['DataFieldForAction', UIAnnotationTypes.DataFieldForAction],
    ['DataFieldForActionGroup', UIAnnotationTypes.DataFieldForActionGroup],
    ['CustomMenu', CustomUIAnnotationTypes.ManifestBasedActionMenu]
]);

const OP_ACTION_MENU_DEFINITION: ActionMenuDefinitionName = {
    actionMenuDefinitionName: DefinitionName.ObjectPageCustomActionMenu,
    actionMenuActionsDefinitionName: DefinitionName.ObjectPageCustomActionMenuActions,
    actionName: DefinitionName.ObjectPageToolBarAction
} as const;

const OP_HEADER_ACTION_MENU_DEFINITION: ActionMenuDefinitionName = {
    actionMenuDefinitionName: DefinitionName.ObjectPageHeaderCustomActionMenu,
    actionMenuActionsDefinitionName: DefinitionName.ObjectPageHeaderCustomActionMenuActions,
    actionName: DefinitionName.ObjectPageHeaderAction
} as const;

const LR_ACTION_MENU_DEFINITION: ActionMenuDefinitionName = {
    actionMenuDefinitionName: DefinitionName.CustomActionMenu,
    actionMenuActionsDefinitionName: DefinitionName.CustomActionMenuActions,
    actionName: DefinitionName.ToolBarAction
} as const;

const LR_ACTION_VIEW_MENU_DEFINITION: ActionMenuDefinitionName = {
    actionMenuDefinitionName: DefinitionName.ViewCustomActionMenu,
    actionMenuActionsDefinitionName: DefinitionName.ViewCustomActionMenuActions,
    actionName: DefinitionName.ViewToolBarAction
} as const;

const actionMenuDefinitionNameMap = new Map<DefinitionName, ActionMenuDefinitionName>([
    [DefinitionName.CustomTableActionOP, OP_ACTION_MENU_DEFINITION],
    [DefinitionName.CustomFormActionOP, OP_ACTION_MENU_DEFINITION],
    [DefinitionName.CustomTableAction, LR_ACTION_MENU_DEFINITION],
    [DefinitionName.CustomHeaderActionOP, OP_HEADER_ACTION_MENU_DEFINITION],
    [DefinitionName.ViewCustomAction, LR_ACTION_VIEW_MENU_DEFINITION]
]);

/**
 * Returns a comparator function to sort objects by their property index.
 *
 * @returns A comparator function that compares two objects based on their property index.
 */
function sortByPropertyIndex(): (a: [string, unknown], b: [string, unknown]) => number {
    return ([_key1, value1], [_key2, value2]) => value1[SchemaTag.propertyIndex] - value2[SchemaTag.propertyIndex];
}

/**
 * Sort function for properties in schema definitions.
 *
 * @param definition - definition which contains list of properties, unsorted
 * @returns - list of properties, sorted by property index
 */
function sortPropertiesRecords(definition: DefinitionOrBoolean): [string, DefinitionOrBoolean][] {
    definition = typeof definition === 'object' ? definition : {};
    const unsortedObjArr = [...Object.entries(definition.properties)];
    if (!definition.properties || Object.keys(definition.properties).length === 0) {
        return unsortedObjArr;
    }
    return unsortedObjArr.sort(sortByPropertyIndex());
}

/**
 * Method receives extension object and returns title/text.
 *
 * @param extension - custom extension object from manifest.
 * @returns Title/text of custom extension.
 */
function getExtensionText(extension: CustomExtension): string | undefined {
    let text: string;
    if (typeof extension === 'object') {
        if ('header' in extension) {
            text = extension.header;
        } else if ('text' in extension) {
            text = extension.text;
        } else if ('title' in extension) {
            text = extension.title;
        } else if ('label' in extension) {
            text = extension.label;
        }
    } else if (typeof extension === 'string') {
        text = extension;
    }
    return text;
}

/**
 * Adds a custom extension to the right position of a sorted array.
 *
 * @param extension - custom extension
 * @param sortedExtensionsArray - array of all entries(annotation entries and extensions), to be enhanced
 * @param targetIndex - index of the anchor entry
 * @param extId - ID of the new custom extension
 * @param newExtensionReference - schema reference of the new extension
 * @param newExtensionReference.$ref - The reference to the schema definition for the new extension.
 * @param newExtensionReference.description - A description of the new extension.
 */
function addExtensionToSortedArray(
    extension: CustomExtension,
    sortedExtensionsArray: [string, unknown][],
    targetIndex: number,
    extId: string,
    newExtensionReference: { $ref: string; description: string }
): void {
    const placement = extension.position?.placement || v4.Placement.After;
    if (placement === v4.Placement.Before) {
        sortedExtensionsArray.splice(targetIndex, 0, [extId, newExtensionReference]);
    } else {
        sortedExtensionsArray.splice(targetIndex + 1, 0, [extId, newExtensionReference]);
    }
}

/**
 * Adds remaining custom extensions that might self-reference and were not added in previous steps.
 *
 * @param {CustomExtensions} remainingExtensions - list of custom extensions that are left for post-processing
 * @param {string} extId - current id of remaining extensions
 * @param {CustomExtension} extension - current extension of remaining custom extensions
 * @param {[string, unknown]} sortedExtArray - sorted array of annotation-based extensions, to be updated
 * @param newCustomExt - description based on extension in manifest
 * @param newCustomExt.$ref - The reference to the schema definition for the new extension.
 * @param newCustomExt.description - A description of the new custom extension.
 * @param newCustomExt.keys - An array of key objects, each containing a name and value for the custom extension.
 */
function addRemainingTargetByBefore(
    remainingExtensions: CustomExtensions,
    extId: string,
    extension: CustomExtension,
    sortedExtArray: [string, unknown][],
    newCustomExt: { $ref: string; description: string; keys: Array<object> }
) {
    const targetRemainingIndex = Object.entries(remainingExtensions)
        .filter(([key]) => key !== extId)
        .findIndex(
            ([_key, entry]) =>
                entry.position?.placement === 'Before' && entry.position?.anchor === extension.position?.anchor
        );
    if (targetRemainingIndex === -1) {
        sortedExtArray.splice(sortedExtArray.length, 0, [extId, newCustomExt]);
        delete remainingExtensions[extId];
    }
}

interface ExtensionDetails {
    $ref: string;
    description: string;
    keys: SchemaDefinitionKey[];
}

/**
 * Returns custom extension details like $ref, description, keys and action type.
 *
 * @param extension - custom extensions config
 * @param extensionDefinitionName - custom extension definition name, different in LR and OP
 * @param extId - custom extension id
 * @param actionType - action type
 * @returns Custom extension details
 */
function getExtensionDetails(
    extension: CustomExtension,
    extensionDefinitionName: string,
    extId: string,
    actionType?: ActionType
): ExtensionDetails {
    const refDefinition = extensionDefinitionName.includes(DefinitionName.ObjectPageCustomSectionFragment)
        ? `${extensionDefinitionName}<${extId}>`
        : extensionDefinitionName;
    const description = getExtensionText(extension);
    return {
        $ref: `${DEFINITION_LINK_PREFIX}${refDefinition}`,
        description,
        keys: [{ name: SchemaKeyName.key, value: `${extId}` }],
        ...(actionType !== undefined && { actionType })
    } as ExtensionDetails;
}

/**
 * Post-processing of custom extensions that are left after the first loop of adding extensions, i.e. custom extensions referring to other custom extensions.
 *
 * @param {CustomExtensions} remainingExtensions - An object containing the remaining extensions to process.
 * @param {string} extensionDefinitionName - The name of the extension definition to refer to.
 * @param {[string, unknown][]} sortedExtArray - A sorted array of existing extensions represented as key-value pairs.
 * @param {ExtensionLogger} logger - The logger used for logging errors and warnings during the adjustment process.
 * @param {boolean} [forceAdd] - Flag indicating whether to force adding extensions even if proper anchor/placement is not found.
 */
function adjustCustomExtReferringToCustomExt(
    remainingExtensions: CustomExtensions,
    extensionDefinitionName: string,
    sortedExtArray: [string, unknown][],
    logger: ExtensionLogger,
    forceAdd = false
): void {
    const initialRemaining = Object.entries(remainingExtensions).length;
    for (const extId of Object.keys(remainingExtensions)) {
        // make sure that we do not add the same extension twice
        const isAlreadyAdded = sortedExtArray.find((element) => element[0] === extId) !== undefined;
        if (isAlreadyAdded) {
            delete remainingExtensions[extId];
            continue;
        }

        const extension = remainingExtensions[extId];
        // Detect description based on extension in manifest
        const newCustomExt = getExtensionDetails(extension, extensionDefinitionName, extId, ActionType.Custom);

        if (extension.position?.anchor && extension.position?.placement) {
            const targetIndex = sortedExtArray.findIndex((element) => element[0] === extension.position.anchor);
            if (targetIndex > -1) {
                addExtensionToSortedArray(extension, sortedExtArray, targetIndex, extId, newCustomExt);
                delete remainingExtensions[extId];
            } else if (
                extId === extension.position.anchor ||
                Object.keys(remainingExtensions).indexOf(extension.position.anchor) === -1
            ) {
                log(logger, {
                    severity: LogSeverity.Error,
                    message: i18next.t('INVALIDANCHOR', { id: extId, anchor: extension.position.anchor }),
                    location: {
                        path: MANIFESTPATH,
                        range: [ManifestSection.ui5]
                    }
                });
                sortedExtArray.splice(sortedExtArray.length, 0, [extId, newCustomExt]);
                delete remainingExtensions[extId];
            } else if (forceAdd) {
                addRemainingTargetByBefore(remainingExtensions, extId, extension, sortedExtArray, newCustomExt);
            }
        } else if (forceAdd) {
            addExtensionToSortedArray(extension, sortedExtArray, sortedExtArray.length, extId, newCustomExt);
            delete remainingExtensions[extId];
        }
    }
    const endRemaining = Object.entries(remainingExtensions).length;
    if (endRemaining > 0 && initialRemaining !== endRemaining) {
        adjustCustomExtReferringToCustomExt(remainingExtensions, extensionDefinitionName, sortedExtArray, logger);
    } else if (endRemaining > 0) {
        adjustCustomExtReferringToCustomExt(remainingExtensions, extensionDefinitionName, sortedExtArray, logger, true);
    }
}

/**
 * Returns anchor for extension. Priority:
 * 1. 'anchor' property in extension;
 * 2. When 'anchor' is not defined in extension and does calculation based placement and current list of extension and annotation entries.
 *
 * @param extension - extension in manifest
 * @param sortedExtensionsArray - sorted array of annotation-based nodes, to be enhanced by extensions
 * @returns Anchor for extension
 */
function getExtensionAnchor(extension: CustomExtension, sortedExtensionsArray: [string, unknown][]): string {
    // Default placement is After
    const placement = extension.position?.placement || v4.Placement.After;
    let anchor = extension.position?.anchor;
    if (!anchor) {
        // Simulate runtime behavior - if there no anchor, then logic uses first or last annotation node
        // In some cases last extension can be previously inserted extension - in such case we still need use annotation node
        const index = placement === v4.Placement.Before ? 0 : sortedExtensionsArray.length - 1;
        anchor = sortedExtensionsArray[index] ? sortedExtensionsArray[index][0] : undefined;
    }
    return anchor;
}

/**
 * Preprocesses and returns list of local extension ids that needs to be skipped from addition.
 *
 * @param {CustomExtensions} localExtensions - list of extensions in manifest
 * @param {[string, unknown][]} annotationEntries - sorted array of annotation-based nodes, to be enhanced by extensions
 * @returns List of local extensions that has no annotation based anchor
 */
function getLocalExtensionsToSkip(localExtensions: CustomExtensions, annotationEntries: [string, unknown][]): string[] {
    // collect local extensions that has no annotation based anchor
    const ignoredIds = [];
    for (const extensionId in localExtensions) {
        const anchor = localExtensions[extensionId].position?.anchor;
        const hasAnnotationAnchor = annotationEntries.some(
            (annotationEntry: [string, unknown]) => annotationEntry[0] === anchor
        );
        if (anchor && !hasAnnotationAnchor) {
            ignoredIds.push(extensionId);
        }
    }
    return ignoredIds;
}

/**
 * Method returns position of passed extension using calculation for merged annotation nodes approach.
 * Currently is used for custom subsections - annotation merges subsection if there is no any child collecation facet.
 *
 * @param extension - custom extension
 * @param extensions - list of extensions in manifest
 * @param sortedExtensionsArray - array of all entries(annotation entries and extensions), to be enhanced
 * @returns Index of extension using merged annotation nodes approach.
 */
function getExtensionIndexByMergedApproach(
    extension: CustomExtension,
    extensions: CustomExtensions,
    sortedExtensionsArray: [string, unknown][]
): number {
    const placement = extension.position?.placement || v4.Placement.After;
    const originalAnchor = extension.position?.anchor;
    if (originalAnchor) {
        // Custom extension is anchored to any extension
        return sortedExtensionsArray.length;
    }
    // Custom extension is not anchored to any extension
    // Inner method to find next available index before or after annotation node
    const findNextIndex = (index: number, condition: (innerExtension?: CustomExtension) => boolean): number => {
        for (const sortExtension of sortedExtensionsArray) {
            const innerExtension = extensions[sortExtension[0]];
            if (condition(innerExtension)) {
                // Condition is fulfilled - no need to increase index
                break;
            }
            // Increase index and check next ordered extension
            index++;
        }
        return index;
    };
    let index = 0;
    if (placement === v4.Placement.Before) {
        // Special case when no anchor but placement is "Before" - then rendered as very first
        // Find index before first annotation node
        index = findNextIndex(0, (innerExtension: CustomExtension) => !innerExtension);
    } else {
        // Special case when no anchor but placement is "After" - then rendered right after last annotation node
        index = findNextIndex(
            -1,
            (innerExtension: CustomExtension) =>
                !!(
                    innerExtension &&
                    // Ignore extension without anchor but with position "Before"
                    (innerExtension?.position?.anchor || innerExtension?.position?.placement !== v4.Placement.Before)
                )
        );
    }
    return index > -1 ? index : 0;
}

/**
 *
 * @param extension - custom extension
 * @param extensions - list of extensions in manifest
 * @param sortedExtensionsArray - array of all entries(annotation entries and extensions), to be enhanced
 * @param anchor - target anchor to check
 * @param ignoreAnnotationAnchors - calculation should ignore anchors referenced to annotation nodes. Used when annotations sections are merged
 * @returns Index of extension using merged annotation nodes approach.
 */
function getExtensionIndex(
    extension: CustomExtension,
    extensions: CustomExtensions,
    sortedExtensionsArray: [string, unknown][],
    anchor: string,
    ignoreAnnotationAnchors = false
): number {
    return !ignoreAnnotationAnchors
        ? sortedExtensionsArray.findIndex((element) => element[0] === anchor)
        : getExtensionIndexByMergedApproach(extension, extensions, sortedExtensionsArray);
}

/**
 * Error handler for invalid extensions.
 *
 * @param logger - logger for error messages
 * @param extensionId - ID of the extension
 * @param sortedExtensionsArray - sorted array of annotation-based nodes, to be enhanced by extensions
 * @param newExtensionReference - schema reference that had been prepared before
 * @param newExtensionReference.$ref - The reference to the schema definition for the new extension.
 * @param newExtensionReference.description - A description of the new extension.
 * @param newExtensionReference.keys - An array of key objects, each containing a name and value for the custom extension.
 * @param newExtensionReference.actionType - The action type associated with the new extension.
 * @param localExtensions - list of extensions for processing
 */
function handleInvalidExtension(
    logger: ExtensionLogger,
    extensionId: string,
    sortedExtensionsArray: [string, unknown][],
    newExtensionReference: ExtensionDetails,
    localExtensions: { [x: string]: any }
) {
    log(logger, {
        severity: LogSeverity.Error,
        message: i18next.t('INVALIDEXTENSION', { id: extensionId }),
        location: {
            path: MANIFESTPATH
        }
    });
    sortedExtensionsArray.push([extensionId, newExtensionReference]);
    delete localExtensions[extensionId];
}

/**
 * Checks if an extension ID represents an annotation-based action.
 * Annotation-based actions should be included in the schema even if they lack custom action
 * properties (press, menu), so they can be validated and removed during export if they don't match the config.
 *
 * @param {string} extensionId - The extension ID to check.
 * @returns {boolean} True if the extension is an annotation-based action, false otherwise.
 */
function isAnnotationBasedAction(extensionId: string): boolean {
    return ANNOTATION_BASED_ACTION_PREFIXES.some((prefix) => extensionId.startsWith(prefix));
}

/**
 * Handles orphaned annotation-based actions that exist in manifest but not in annotations.
 * These actions must be added to the schema to enable cleanup during export.
 *
 * @param {string} extensionId - The extension ID.
 * @param {any} extension - The extension object.
 * @param {string} extensionDefinitionName - The definition name associated with the extensions.
 * @param {Definition} [appSchema] - Optional application schema for creating specific definitions.
 * @param {ActionDetails[]} sortedExtensionsArray - The array to add the action to.
 */
function handleOrphanedAnnotationAction(
    extensionId: string,
    extension: any,
    extensionDefinitionName: string,
    appSchema: Definition | undefined,
    sortedExtensionsArray: ActionDetails[]
): void {
    // This is an annotation-based action from manifest that doesn't exist in annotations.
    // IMPORTANT: We must add these orphaned actions to the schema to enable cleanup.
    //
    // When an action is renamed (e.g., IBN action's semantic object changes), the old
    // action key remains in the manifest but disappears from the config (annotations).
    // By adding it to the schema, the export process can detect this mismatch and remove
    // the orphaned entry from the manifest. If we don't add it to the schema, it will be
    // ignored and remain as garbage in the manifest forever.
    //
    // Process:
    // 1. Add orphaned action to schema with Standard action type
    // 2. During export: compare schema with config
    // 3. Action in schema but NOT in config → remove from manifest
    // 4. Action in schema AND in config → keep in manifest

    if (appSchema) {
        // Create specific definition name and schema like "ToolBarActionGroup<DataFieldForActionGroup::AnnoActionMenu>"
        const { actionName } = determineActionDefinitionNames(extensionDefinitionName);
        const preparedRef = prepareRef(extensionId);
        const specificDefinitionName = getFacetDefinitionKey(actionName, preparedRef);

        // Create the schema definition for this action
        parseSchemaDefinition(actionName, preparedRef, appSchema);

        const correctedExtensionReference = getExtensionDetails(
            extension,
            specificDefinitionName,
            extensionId,
            ActionType.Standard
        );
        sortedExtensionsArray.push([extensionId, correctedExtensionReference]);
    } else {
        // Fallback if no appSchema provided
        const correctedExtensionReference = getExtensionDetails(
            extension,
            extensionDefinitionName,
            extensionId,
            ActionType.Standard
        );
        sortedExtensionsArray.push([extensionId, correctedExtensionReference]);
    }
}

/**
 * Recursively checks if an action exists in annotation entries, including nested actions within action groups.
 *
 * @param {string} extensionId - The unique identifier for the extension to search for.
 * @param {ActionDetails[]} annotationEntries - Array of actions to search through.
 * @param {Definition | undefined} appSchema - Optional application schema to look up action group definitions.
 * @returns {boolean} True if the action exists at any level (top-level or nested in groups).
 */
function actionExistsInAnnotations(
    extensionId: string,
    annotationEntries: ActionDetails[],
    appSchema: Definition | undefined
): boolean {
    for (const [id, actionDef] of annotationEntries) {
        // Check top-level match
        if (id === extensionId) {
            return true;
        }

        // Check if this is an action group with nested actions
        const actionDefinition = actionDef as Definition;
        if (appSchema && actionDefinition.$ref) {
            const defKey = actionDefinition.$ref.replace('#/definitions/', '');
            const definition = appSchema.definitions?.[defKey] as Definition;

            // If it's an action group, check its nested actions (properties)
            if (definition && definition.properties) {
                const nestedActions = Object.entries(definition.properties).map(
                    ([nestedId, nestedDef]) => [nestedId, nestedDef] as ActionDetails
                );

                // Recursively search nested actions
                if (actionExistsInAnnotations(extensionId, nestedActions, appSchema)) {
                    return true;
                }
            }
        }
    }

    return false;
}

/**
 * Processes annotation-based actions with unsupported extension types.
 * Either skips duplicates or handles orphaned actions.
 *
 * @param {string} extensionId - The extension ID.
 * @param {any} extension - The extension object.
 * @param {string} extensionDefinitionName - The definition name.
 * @param {Definition} [appSchema] - Optional application schema.
 * @param {ActionDetails[]} annotationEntries - Array of annotation entries.
 * @param {ActionDetails[]} sortedExtensionsArray - The sorted extensions array.
 */
function processAnnotationBasedAction(
    extensionId: string,
    extension: any,
    extensionDefinitionName: string,
    appSchema: Definition | undefined,
    annotationEntries: ActionDetails[],
    sortedExtensionsArray: ActionDetails[]
): void {
    // Check if this annotation-based action exists in annotationEntries (including nested in action groups)
    const existsInAnnotations = actionExistsInAnnotations(extensionId, annotationEntries, appSchema);
    if (existsInAnnotations) {
        // Action already exists from annotations - skip it to avoid duplicates
        return;
    }

    // Handle orphaned annotation-based action
    handleOrphanedAnnotationAction(extensionId, extension, extensionDefinitionName, appSchema, sortedExtensionsArray);
}

/**
 * Adds extension entries to a sorted array of extensions based on specific rules,
 * including using anchors, callbacks, and custom templates.
 *
 * @param {CustomExtensions} extensions - The object containing custom extensions to be added to the array.
 * @param {ActionDetails[]} sortedExtensionsArray - The array of sorted extensions to which new extensions will be added.
 * @param {string} extensionDefinitionName - The definition name associated with the extensions being added.
 * @param {TemplatePropertyName | TemplatePropertyName[]} [templatePropertyName] -
 * The property name(s) to check for existence in an extension; used to determine compatibility.
 * @param {ExtensionLogger} [logger] - An optional logger to handle logging of invalid extensions or other events.
 * @param {boolean} [ignoreAnnotationAnchors=false] - A flag to indicate whether extension anchors should be ignored.
 * @param {Definition} [appSchema] - Optional application schema for creating specific definitions for annotation-based actions.
 * @returns {ActionDetails[]} The updated sorted extensions array, including newly added and valid extensions.
 */
export function addExtensionToArray(
    extensions: CustomExtensions,
    sortedExtensionsArray: ActionDetails[],
    extensionDefinitionName: string,
    templatePropertyName: TemplatePropertyName | TemplatePropertyName[] = TemplatePropertyName.Template,
    logger?: ExtensionLogger,
    ignoreAnnotationAnchors = false,
    appSchema?: Definition
): ActionDetails[] {
    templatePropertyName = Array.isArray(templatePropertyName) ? templatePropertyName : [templatePropertyName];

    const annotationEntries = [...sortedExtensionsArray];
    const localExtensions = { ...extensions };
    const localExtensionsToSkip = getLocalExtensionsToSkip(localExtensions, annotationEntries);

    if (!localExtensions) {
        return [];
    }

    for (const extensionId of Object.keys(localExtensions)) {
        const extension = localExtensions[extensionId];
        if (localExtensionsToSkip?.includes(extensionId)) {
            continue;
        }

        const newExtensionReference = getExtensionDetails(
            extension,
            extensionDefinitionName,
            extensionId,
            ActionType.Custom
        );
        if (typeof extension !== 'object') {
            handleInvalidExtension(logger, extensionId, sortedExtensionsArray, newExtensionReference, localExtensions);
            continue;
        }

        const hasUnsupportedExtensionType = templatePropertyName.every((propertyName) => !(propertyName in extension));
        if (hasUnsupportedExtensionType) {
            const isAnnotationBased = isAnnotationBasedAction(extensionId);
            if (isAnnotationBased) {
                processAnnotationBasedAction(
                    extensionId,
                    extension,
                    extensionDefinitionName,
                    appSchema,
                    annotationEntries,
                    sortedExtensionsArray
                );
                delete localExtensions[extensionId];
                continue;
            } else {
                // Skip regular entries (columns, actions, etc.) that don't have required properties
                delete localExtensions[extensionId];
                continue;
            }
        }

        const anchor = getExtensionAnchor(extension, annotationEntries);
        if (anchor) {
            const targetIndex = getExtensionIndex(
                extension,
                extensions,
                sortedExtensionsArray,
                anchor,
                ignoreAnnotationAnchors
            );
            if (targetIndex > -1) {
                addExtensionToSortedArray(
                    extension,
                    sortedExtensionsArray,
                    targetIndex,
                    extensionId,
                    newExtensionReference
                );
                delete localExtensions[extensionId];
            }
        } else {
            // Empty array - no any anchor to reference to
            sortedExtensionsArray.push([extensionId, newExtensionReference]);
            delete localExtensions[extensionId];
        }
    }
    if (Object.keys(localExtensions).length > 0) {
        //Second loop is necessary for custom columns that refer to custom columns
        adjustCustomExtReferringToCustomExt(
            localExtensions,
            extensionDefinitionName,
            sortedExtensionsArray,
            logger,
            false
        );
    }
    return sortedExtensionsArray;
}

/**
 * Returns column names for enum.
 *
 * @param lineItems - current lineItems definition, with all records.
 * @returns Column names.
 */
function getColumnsEnum(lineItems: boolean | Definition): string[] {
    const columnEnum = [];
    for (const key of Object.keys(lineItems as Definition)) {
        if (key.indexOf(FIELD_SEPARATOR) > -1) {
            columnEnum.push(key.substring(key.indexOf(FIELD_SEPARATOR) + 2));
        }
    }
    return columnEnum;
}

/**
 * Removes the field prefix from a given key based on the defined FIELD_SEPARATOR.
 *
 * @param {string} key - The string key that contains a prefix and a value separated by FIELD_SEPARATOR.
 * @returns {string} The key without its prefix, retaining the portion after the FIELD_SEPARATOR.
 */
function removeDataFieldPrefix(key: string): string {
    const [_, ...value] = key.split(FIELD_SEPARATOR);
    return value.join(FIELD_SEPARATOR);
}

/**
 * Extracts and processes the columns defined in the given lineItems or Definition object,
 * returning an array of CreationFieldOneOfOption.
 *
 * @param {Definition} [columns] - An object containing column definitions where keys represent field identifiers
 *                                     and values are field metadata.
 * @returns {CreationFieldOneOfOption[]} An array of field options, each containing field-specific metadata.
 */
function getCreationFieldOptions(columns: Definition = {}): CreationFieldOneOfOption[] {
    const columnOneOfEnum: CreationFieldOneOfOption[] = [];
    // Extracts fields from line item properties, excluding field groups (they are handled separately)
    for (const key of Object.keys(columns)) {
        const columnName = removeDataFieldPrefix(key);
        if (columnName && !columnName.includes(CreationFieldType.FieldGroup)) {
            columnOneOfEnum.push({
                const: columnName,
                description: columns[key].description,
                groupType: CreationFieldType.Field
            });
        }
    }
    return columnOneOfEnum;
}

/**
 * Extracts field group options from the annotations of the provided entity type.
 *
 * @param {EntityType} [entityType] The entity type which contains annotations from which field groups are derived.
 * @returns {CreationFieldOneOfOption[]} An array of field group options extracted from the entity type annotations.
 */
function getFieldGroupsFromEntityType(entityType?: EntityType): CreationFieldOneOfOption[] {
    const uiAnnotations: EntityTypeAnnotations_UI = entityType?.annotations?.[UI_ANNOTATION_KEY] ?? {};
    const annotationKeys = Object.keys(uiAnnotations);
    const fieldGroupKeys = annotationKeys.filter((key) =>
        key.startsWith(`${CreationFieldType.FieldGroup}${QUALIFIER_SEPARATOR}`)
    );
    return fieldGroupKeys.map((key) => {
        const { term, qualifier, Label: label } = uiAnnotations[key];
        const option: CreationFieldOneOfOption = {
            const: `${term}${FIELD_SEPARATOR}${qualifier}`,
            groupType: CreationFieldType.FieldGroup
        };
        if (label) {
            option.description = label;
        }
        return option;
    });
}

/**
 * Adds enum values to the properties definition of a custom column.
 *
 * @param lineItems - current lineItems definition, with all records.
 * @param customColumnDefinition - current custom column definition, in the app schema
 */
export function addEnumForProperties(lineItems: boolean | Definition, customColumnDefinition: Definition) {
    (customColumnDefinition.properties.properties as Definition).items['enum'] = getColumnsEnum(
        (lineItems as Definition).properties
    );
}

/**
 * Adds the propertyIndex to each column and adds the enum values for properties of custom columns.
 *
 * @param sortedColumnsArray - sorted array of all columns
 * @param {Definition} appSchema - app specific JSON schema
 * @param lineItems - content of the line item annotation
 * @param {DefinitionName} customColumnDefinitionName - custom column definition name, distinguishes LR from OP
 */
function addColumnPropertyIndexAndEnum(
    sortedColumnsArray: [string, DefinitionOrBoolean][],
    appSchema: Definition,
    lineItems: boolean | Definition,
    customColumnDefinitionName: string = DefinitionName.CustomColumn
) {
    if (
        customColumnDefinitionName === DefinitionName.CustomColumn ||
        customColumnDefinitionName.indexOf(DefinitionName.ViewCustomColumn) > -1
    ) {
        //Table custom column: Add enum for properties
        const customColumnDefinition = appSchema.definitions[customColumnDefinitionName] as Definition;
        addEnumForProperties(lineItems, customColumnDefinition);
    }

    //Add property index
    for (let index = 0; index < sortedColumnsArray.length; index++) {
        const column = sortedColumnsArray[index][1] as Definition;
        column[SchemaTag.propertyIndex] = index;
    }
}

/**
 * Common function for enhancing the LineItems definition of app schema by custom columns.
 *
 * @param appSchema - app-specific JSON schema
 * @param v4Page - actual page in the manifest
 * @param logger - logger for error messages
 * @param customColumnDefinitionName - custom column definition name, distinguishes LR from OP
 * @param sectionId - identifier of the current object page section in schema
 * @param sectionIdInManifest - identifier of the current object page section in manifest
 */
export function addCustomColumnDefinition(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    logger: ExtensionLogger,
    customColumnDefinitionName: string = DefinitionName.CustomColumn,
    sectionId?: string,
    sectionIdInManifest?: string
): void {
    const lineItemId = sectionId || DefinitionName.LineItems;
    const lineItems = appSchema.definitions[lineItemId];
    if (!lineItems) {
        return;
    }
    //sort line items
    let sortedColumnsArray = sortPropertiesRecords(lineItems);

    const id = sectionIdInManifest ? sectionIdInManifest.replace(/::/g, '/') : `@${UIAnnotationTerms.LineItem}`;
    const columns = v4Page.options?.settings?.controlConfiguration?.[id]?.['columns'];
    if (columns) {
        sortedColumnsArray = addExtensionToArray(
            columns,
            sortedColumnsArray,
            customColumnDefinitionName,
            undefined,
            logger
        );
    }
    //Adjust propertyIndex and add properties' enum
    addColumnPropertyIndexAndEnum(sortedColumnsArray, appSchema, lineItems, customColumnDefinitionName);

    const sortedColumnsAsObject = {};
    sortedColumnsArray.forEach(([key, value]) => (sortedColumnsAsObject[key] = value));
    (appSchema.definitions[lineItemId] as Definition).properties = sortedColumnsAsObject;
}

/**
 * Determines the appropriate action definition name based on the provided line item data and record type.
 *
 * @param {LineItemDataForSchema} data - The schema data for the line item, including its identifier and metadata.
 * @param {DataFieldAbstractTypes} lineItemRecord - The record representing a data field or action within the line item.
 * @param {boolean} isInnerAction - Whether the action belongs to the action group.
 * @returns {DefinitionName} The determined action definition name based on the provided data and group type.
 */
function determineActionDefinitionName(
    data: LineItemDataForSchema,
    lineItemRecord: DataFieldAbstractTypes,
    isInnerAction?: boolean
): DefinitionName {
    const isGrouped = lineItemRecord.$Type === UIAnnotationTypes.DataFieldForActionGroup;
    let actionDefinitionName: DefinitionName;
    if (data.lineItemId.startsWith(DefinitionName.LineItemsOfView)) {
        actionDefinitionName = isGrouped
            ? DefinitionName.ViewToolBarActionGroup
            : isInnerAction
              ? DefinitionName.ViewToolBarActionBase
              : DefinitionName.ViewToolBarAction;
    } else if (data.lineItemId.startsWith(DefinitionName.LineItems)) {
        actionDefinitionName = isGrouped
            ? DefinitionName.ToolBarActionGroup
            : isInnerAction
              ? DefinitionName.ToolBarActionBase
              : DefinitionName.ToolBarAction;
    } else {
        actionDefinitionName = isGrouped
            ? DefinitionName.ObjectPageToolBarActionGroup
            : isInnerAction
              ? DefinitionName.ObjectPageToolBarActionBase
              : DefinitionName.ObjectPageToolBarAction;
    }
    return actionDefinitionName;
}

/**
 * Adds the schema tags 'keys' and 'target' to the schema definition of an action.
 *
 * @param {SchemaDefinition} actionDefinition - current action definition in the app schema
 * @param {DataFieldForActionAbstractTypes} lineItemRecord - actual record of the line item collection
 * @param {string} actionId - ID of the action, according to the lineItem definition
 */
function addTargetAndKeysOfAction(
    actionDefinition: SchemaDefinition,
    lineItemRecord: DataFieldForActionAbstractTypes,
    actionId: string
): void {
    actionDefinition[SchemaTag.keys] = [];
    if (lineItemRecord[SchemaKeyName.semanticObject]) {
        actionDefinition[SchemaTag.keys].push({
            name: SchemaKeyName.semanticObject,
            value: lineItemRecord[SchemaKeyName.semanticObject]
        });
    }
    const { key, ns } = getActionKey(actionId, true);
    actionDefinition[SchemaTag.keys].push({ name: SchemaKeyName.action, value: key });

    if (ns) {
        actionDefinition[SchemaTag.target] = ns;
    }
}

/**
 * Adds an action record to a schema definition based on the provided line item definition parameters.
 * This involves defining action-specific properties, references, and metadata, and updating the schema accordingly.
 *
 * @param {AddLineItemDefinitionParams} lineItemDefinitionParams - The parameters describing the line item definition, including data, actions, path, record, and index.
 * @param {boolean} [createRef] - A flag indicating whether to create a reference for the action definition.
 * @param {boolean} isInnerAction - Whether the action belongs to the action group.
 */
export function addActionRecordToSchema(
    lineItemDefinitionParams: AddLineItemDefinitionParams,
    createRef = true,
    isInnerAction = false
): void {
    const {
        lineItemData: data,
        actions: actionsDefinition,
        lineItemPath,
        lineItemRecord,
        index: recordIndex
    } = lineItemDefinitionParams;

    // Action but not inline action
    if (lineItemRecord.Determining === true) {
        return;
    }
    //no footer bar -> toolbar action
    const actionDefinitionName = determineActionDefinitionName(data, lineItemRecord, isInnerAction);
    const description = getDataFieldDescription(lineItemRecord as DataFieldAbstractTypes, data.entityType);

    // Generate stable ID using strategy pattern to ensure correct key format for all action types.
    // This is crucial for IBN actions, which need the full key including SemanticObject and RequiresContext
    // (e.g., "DataFieldForIntentBasedNavigation::v4Freestyle::Inbound::RequiresContext")
    // to be properly matched when referenced in manifest-based action menus.
    const strategy = dataFieldStrategyContext.getStrategy(lineItemRecord.$Type);
    const stableId = strategy.generateStableId(lineItemRecord);

    // Extract Action property for keys generation (used by addTargetAndKeysOfAction).
    // For IBN actions, this is just the action name (e.g., "Inbound").
    // For bound actions, this may include namespace (e.g., "com.sap.Action/MyAction").
    const actionId = typeof lineItemRecord.Action === 'string' ? lineItemRecord.Action : lineItemRecord.Action['path'];

    // Remove type prefix from stable ID for use in $ref generation.
    // This ensures getUniqueFacetDefinitionLink can properly detect duplicates based on full action identity,
    // not just the Action property. For example, two IBN actions with different SemanticObjects but same Action
    // should be treated as different actions, not duplicates.
    const stableIdWithoutPrefix = stableId.split('::').slice(1).join('::');

    // Create action definition with full stable ID as property key.
    // This allows manifest-based menus to correctly reference actions by their complete identity.
    const actionDefinition: SchemaDefinition = (actionsDefinition.properties[stableId] = {
        description
    });
    if (createRef) {
        actionDefinition.$ref = getUniqueFacetDefinitionLink(
            actionDefinitionName,
            prepareRef(stableIdWithoutPrefix),
            data.appSchema,
            lineItemPath
        );
    } else {
        actionDefinition.properties = {};
        actionDefinition.type = 'object';
        actionDefinition.isViewNode = true;
    }
    actionDefinition[SchemaTag.propertyIndex] = recordIndex;
    actionDefinition[SchemaTag.dataType] = determineDataType(lineItemRecord);

    //keys
    addTargetAndKeysOfAction(actionDefinition, lineItemRecord, actionId);

    actionDefinition[SchemaTag.annotationType] = lineItemRecord.$Type;
    if (createRef) {
        // Using previously computed ref instead of generating new one to support action duplication
        const previouslyComputedRef = actionDefinition.$ref.replace(DEFINITION_LINK_PREFIX, '');
        const action = parseSchemaDefinition(actionDefinitionName, previouslyComputedRef, data.appSchema, false);
        action[SchemaTag.annotationPath] = `${lineItemPath}/${recordIndex}`;
    } else {
        actionDefinition[SchemaTag.annotationPath] = `${lineItemPath}/${recordIndex}`;
    }
}

/**
 * Adds a group action record to the schema based on the given parameters. This involves determining
 * action definitions, creating or referencing schema properties, and setting metadata fields.
 *
 * @param {AddLineItemDefinitionParams} lineItemDefinitionParams - Parameters that define the line item, including data, record, path, index, and actions.
 * @param {boolean} [createRef] - Indicates whether to create a reference for the action definition or to use an inline object.
 */
export function addGroupActionRecordToSchema(
    lineItemDefinitionParams: AddLineItemDefinitionParams,
    createRef = true
): void {
    const { lineItemData, lineItemRecord, lineItemPath, index, actions } = lineItemDefinitionParams;

    //no footer bar -> toolbar action
    const actionDefinitionName = determineActionDefinitionName(lineItemData, lineItemRecord);
    const propertyKey = getStableIdPartFromDataField(lineItemRecord);

    let actionDefinition: SchemaDefinition = {};

    if (createRef) {
        actions.properties[`${propertyKey}`] = {
            $ref: getFacetDefinitionLink(actionDefinitionName, prepareRef(propertyKey))
        };
        actionDefinition = parseSchemaDefinition(actionDefinitionName, prepareRef(propertyKey), lineItemData.appSchema);
    } else {
        actions.properties[`${propertyKey}`] = actionDefinition;
    }

    if (!actionDefinition.properties) {
        actionDefinition.properties = {};
    }
    actionDefinition.description = getDataFieldDescription(
        lineItemRecord as DataFieldAbstractTypes,
        lineItemData.entityType
    );
    actionDefinition.type = 'object';
    actionDefinition.isViewNode = true;

    actionDefinition[SchemaTag.propertyIndex] = index;
    actionDefinition[SchemaTag.dataType] = determineDataType(lineItemRecord);
    actionDefinition[SchemaTag.annotationPath] = `${lineItemPath}/${index}`;

    (lineItemRecord['Actions'] ?? []).forEach((action, index) => {
        const lineItemActionDefinitionParams = {
            ...lineItemDefinitionParams,
            lineItemRecord: action,
            lineItemPath: `${actionDefinition[SchemaTag.annotationPath]}/Actions`,
            actions: actionDefinition,
            index
        };
        addActionRecordToSchema(lineItemActionDefinitionParams, createRef, true);
    });
}

/**
 * Adds a line item action definition to the schema based on the provided parameters.
 * Handles determining which action to take based on specific conditions of the line item record.
 *
 * @param {AddLineItemDefinitionParams} data - An object containing the details of the line item record and the line item definition.
 * @param {object} data.lineItemRecord - The record of the line item to be processed, containing relevant properties.
 * @param {boolean} data.lineItemRecord.Inline - Indicates whether the line item is inline.
 * @param {boolean} data.lineItemRecord.Determining - Indicates if the line item is determining.
 * @param {boolean} data.lineItemDefinition - Indicates whether the line item definition is present.
 */
export function addLineItemActionDefinition(data: AddLineItemDefinitionParams) {
    const { lineItemRecord, lineItemDefinition } = data;

    if (lineItemRecord.Inline !== true || lineItemRecord.Determining === true) {
        addActionRecordToSchema(data, !lineItemDefinition);
    } else {
        addLineItemRecordToSchema(data);
    }
}
/**
 * Adds a line item record to the provided schema based on the definition parameters.
 *
 * @param {AddLineItemDefinitionParams} lineItemDefinitionParams - The parameters defining the line item data, schema information, and record.
 */
export function addLineItemRecordToSchema(lineItemDefinitionParams: AddLineItemDefinitionParams) {
    const { lineItemData, lineItemRecord, lineItemDefinition, lineItemPath, index, columnDefinitionName } =
        lineItemDefinitionParams;
    const columnDefinition =
        lineItemRecord.$Type === UIAnnotationTypes.DataFieldForAction
            ? `${columnDefinitionName}Action`
            : columnDefinitionName;
    const lineItemId = lineItemData.lineItemId;

    if (lineItemRecord['Target']?.['value']) {
        const regex = `@${findAlias(UIVOCABULARY, lineItemData.oDataServiceAVT)}.`;
        lineItemRecord['Target']['value'] = lineItemRecord['Target']['value'].replace(regex, '@');
    }
    const schemaKey = getStableIdPartFromDataField(lineItemRecord).replace(/\//g, FIELD_SEPARATOR);
    if (schemaKey) {
        let columnsDefinition = lineItemDefinition;
        if (!columnsDefinition) {
            columnsDefinition = lineItemData.appSchema.definitions[lineItemId] as Definition;
        }
        const properties = columnsDefinition.properties;
        const property: SchemaDefinition = {
            description: getDataFieldDescription(lineItemRecord, lineItemData.entityType)
        };
        if (lineItemDefinition) {
            property.properties = {};
            property.type = 'object';
            property.isViewNode = true;
        } else {
            property.$ref = DEFINITION_LINK_PREFIX + columnDefinition;
        }
        property[SchemaTag.annotationType] = lineItemRecord?.$Type;
        property[SchemaTag.annotationPath] = `${lineItemPath}/${index}`;
        property[SchemaTag.propertyIndex] = index;
        property[SchemaTag.dataType] = determineDataType(lineItemRecord);
        properties[schemaKey] = property;
        addKeyToDefinition(lineItemRecord, properties, schemaKey);
    }
}

export type LineItemDataForSchema = {
    isObjectPage: boolean;
    isMacro?: boolean;
    appSchema: Definition;
    lineItemAnnotation: AnnotationTerm<LineItem>;
    entityType: EntityType;
    oDataServiceAVT: ConvertedMetadata;
    lineItemId: string;
};

/**
 * Appends line item columns and actions to the app schema (for the list report, custom page or an object page section comprising a table).
 *
 * @param {LineItemDataForSchema} data structure comprising
 * - appSchema - the app specific schema that shall get enhanced
 * - lineItemAnnotation - the UI.LineItem annotation, comprising all records
 * - entityType - the entity type as part of the AVT ConvertedMetadata
 * - oDataServiceAVT - complete service information, as returned by Annotation Vocabularies Tool
 * - lineItemId - in case of OP the parameter must be passed to distinguish the OP tables;
 *                in case of LR or ALP 'LineItems' is passed.
 * @param {Definition} actions - actions definition in schema, parent object
 * @param {string} lineItemPath - annotation path to the line item
 * @param {Definition | undefined} lineItemDefinition - line item definition
 * @param {string} columnDefinitionName - line item definition name
 */
export function appendLineItemsToSchema(
    data: LineItemDataForSchema,
    actions: Definition,
    lineItemPath: string,
    lineItemDefinition?: Definition,
    columnDefinitionName: string = DefinitionName.TableColumn
): void {
    if (!data.lineItemAnnotation) {
        return;
    }

    let i = 0;
    data.lineItemAnnotation.forEach((lineItemRecord: DataFieldAbstractTypes) => {
        const addLineItemDefinitionsParams: AddLineItemDefinitionParams = {
            lineItemData: data,
            actions,
            lineItemPath,
            lineItemRecord: lineItemRecord as DataFieldForActionAbstractTypes,
            index: i,
            lineItemDefinition,
            columnDefinitionName
        };
        const strategy = dataFieldStrategyContext.getStrategy(lineItemRecord.$Type);
        strategy.addLineItemDefinition(addLineItemDefinitionsParams);
        i++;
    });
}

/**
 * Adds the line item definition, columns and actions to the app schema (for the list report, custom page or an object page section comprising a table.
 *
 * @param data structure comprising
 * - isObjectPage - indicates that the page is an object page
 * - appSchema - the app specific schema that shall get enhanced
 * - lineItemAnnotation - the UI.LineItem annotation, comprising all records
 * - entityType - the entity type as part of the AVT ConvertedMetadata
 * - oDataServiceAVT - complete service information, as returned by Annotation Vocabularies Tool
 * - lineItemId - in case of OP the parameter must be passed to distinguish the OP tables;
 *                in case of LR or ALP 'LineItems' is passed.
 * @param columnDefinitionName - name of the column definition, i.e. TableColumn or ObjectPageTableColumn
 * @param customColumnDefinitionName - name of the custom column definition
 * @param customActionDefinitionName - name of the custom action definition
 */
export function addLineItemsTypeToSchema(
    data: LineItemDataForSchema,
    columnDefinitionName: string = DefinitionName.TableColumn,
    customColumnDefinitionName: string = DefinitionName.CustomColumn,
    customActionDefinitionName: string = DefinitionName.CustomTableAction
): void {
    const { actions, lineItemPath } = addCommonLineItemDefinitions(
        data.isObjectPage,
        data.appSchema,
        data.lineItemAnnotation,
        data.entityType,
        data.lineItemId,
        customColumnDefinitionName,
        customActionDefinitionName
    );

    appendLineItemsToSchema(data, actions, lineItemPath, undefined, columnDefinitionName);
}

/**
 * Adds an enum or value help to position > anchor of custom column.
 *
 * @param appSchema - app specific JSON schema
 * @param lineItemId - ID of the current line item in schema
 * @param v4Page - current page in manifest
 * @param positionName - ID of the definition of custom column position in schema
 * @param sectionIdInManifest - identifier of the current object page section in manifest
 */
export function addFragmentEnumForAnchor(
    appSchema: Definition,
    lineItemId: string,
    v4Page: v4.SapUiAppPageV4,
    positionName: string = DefinitionName.Position,
    sectionIdInManifest?: string
): void {
    const columns = (appSchema.definitions[lineItemId] as Definition)?.properties;
    const enumEntries = [];
    if (columns) {
        for (const key in columns) {
            if (key !== 'custom') {
                enumEntries.push(key);
            }
        }
    }
    const id = lineItemId !== DefinitionName.LineItems ? sectionIdInManifest : `@${UIAnnotationTerms.LineItem}`;
    const columnsInManifest = v4Page.options?.settings?.controlConfiguration?.[id]?.['columns'];
    if (columnsInManifest) {
        for (const key in columnsInManifest) {
            if (enumEntries.indexOf(key) === -1) {
                enumEntries.push(key);
            }
        }
    }
    if (enumEntries.length > 0) {
        const anchor = (appSchema.definitions[positionName] as Definition).properties.anchor as Definition;
        addEnumToSchema(enumEntries, anchor);
    }
}

/**
 * Adds an enum or value help to position > anchor of custom column or custom action.
 *
 * @param appSchema - app specific JSON schema
 * @param definitionId - definition key of actions
 * @param positionName - target definition to update
 */
export function addEnumForActionAnchor(
    appSchema: Definition,
    definitionId: string,
    positionName: string = DefinitionName.CustomActionPosition
): void {
    addDescriptiveEnumDefinition(
        appSchema,
        appSchema.definitions[definitionId],
        {
            definition: positionName,
            property: 'anchor'
        },
        {
            resolveDefinition: true
        }
    );
}

/**
 * Adds an enum options of the columns to the table enableMassEdit properties.
 *
 * @param {Definition} appSchema - The schema of the application where the enums will be added.
 * @param {string} tableDefinitionName - The name of the table definition in the schema.
 * @param {string} columnsDefinitionName - The name of the columns definition used to extract column enumerations.
 * @param {EntityType} [entityType] - Optional entity type for additional context when retrieving column definitions.
 */
export function addEnumForEnableMassEdit(
    appSchema: Definition,
    tableDefinitionName: string,
    columnsDefinitionName: string,
    entityType?: EntityType
): void {
    const FIELD_PROPERTIES = ['visibleFields', 'ignoredFields'];
    const columnsDefinitions = getNonNavigationColumnDefinitions(appSchema, columnsDefinitionName, entityType);
    const columnEnums = getColumnsEnum(columnsDefinitions);
    const enableMassEditDefinition = (appSchema.definitions[tableDefinitionName] as Definition).properties[
        'enableMassEdit'
    ] as Definition;
    const enableMassEditFields = enableMassEditDefinition.anyOf.find(
        (definition: Definition) => definition.type === 'object'
    ) as Definition;
    for (const propertyKey in enableMassEditFields.properties) {
        if (FIELD_PROPERTIES.includes(propertyKey)) {
            const property = enableMassEditFields.properties[propertyKey] as Definition;
            property.items['enum'] = columnEnums;
            property.uniqueItems = true;
        }
    }
}

/**
 * Filters and retrieves column definitions that are not related to navigation properties
 * based on the provided application schema, line item ID, and entity type.
 *
 * @param appSchema The application schema definition containing the column definitions.
 * @param lineItemId The identifier of the line item whose column definitions are to be filtered.
 * @param entityType The entity type containing the navigation properties to filter against.
 * @returns A definition object containing column definitions that are not navigation properties.
 */
function getNonNavigationColumnDefinitions(
    appSchema: Definition,
    lineItemId: string,
    entityType?: EntityType
): Definition {
    const columns = (appSchema.definitions[lineItemId] as Definition)?.properties;
    const navigationProperties = new Set((entityType?.navigationProperties ?? []).map((np) => np.name));
    const filteredColumns = {};
    for (const key of Object.keys(columns)) {
        const column = columns[key];
        const propertyKeyValue = (column[SchemaTag.keys] ?? []).reduce((acc, currentKey) => {
            const { name, value } = currentKey;
            return name === SchemaKeyName.value || name === SchemaKeyName.target ? value : acc;
        }, '');
        const isNavigationProperty = propertyKeyValue.split('/').some((value) => navigationProperties.has(value));
        if (!isNavigationProperty) {
            filteredColumns[key] = columns[key];
        }
    }
    return filteredColumns;
}

/**
 * Initializes and modifies a specific schema definition for the creation mode of a line item.
 *
 * @param {Definition} appSchema - The base application schema containing definitions.
 * @param {string} lineItemId - The identifier for the line item in the schema.
 * @param {EntityType} entityType - The metadata entity type related to the line item.
 * @param {string} definitionName - The name of the general schema definition to be adapted.
 * @returns {string} The name of the newly created specific schema definition for the creation mode.
 */
export function initializeCreationModeSchema(
    appSchema: Definition,
    lineItemId: string,
    entityType: EntityType,
    definitionName: string
) {
    const columnDefinitions = getNonNavigationColumnDefinitions(appSchema, lineItemId, entityType);
    const columnOneOfEnums: CreationFieldOneOfOption[] = getCreationFieldOptions(columnDefinitions);
    const fieldGroups = getFieldGroupsFromEntityType(entityType);
    const specificCreationModeDefinitionName = getFacetDefinitionKey(definitionName, lineItemId);
    const specificCreationModeDefinition = (appSchema.definitions[specificCreationModeDefinitionName] = structuredClone(
        appSchema.definitions[definitionName] as Definition
    ));
    const creationFieldsDefinition = specificCreationModeDefinition.properties[
        PropertyName.creationFields
    ] as Definition;
    const creationFieldsDefinitionItems = creationFieldsDefinition.items as SchemaDefinition;
    creationFieldsDefinitionItems['oneOf'] = [...columnOneOfEnums, ...fieldGroups];
    // Entries of one of are translatable using 'service' i18n bundle
    creationFieldsDefinitionItems.i18nBundle = 'service';
    creationFieldsDefinition.uniqueItems = true;
    return specificCreationModeDefinitionName;
}

/**
 * Updates the schema for specific creation fields in the provided application schema.
 * This method dynamically modifies schemas of particular table types to include specific definitions for creation fields.
 *
 * @param appSchema The application schema to update, represented as a Definition object.
 * @param entityType The entity type used for determining field groups.
 * @param definitionName The name of the schema definition that serves as the base for the specific creation mode schema.
 * @param lineItemId The identifier of the line item related to the creation fields to be updated in the schema.
 */
export function addOneOfForCreationFields(
    appSchema: Definition,
    entityType: EntityType,
    definitionName: string,
    lineItemId: string
): void {
    const creationModeDefinitionName = initializeCreationModeSchema(appSchema, lineItemId, entityType, definitionName);

    // Replace the generic creationMode definition reference with the facet-specific one
    const tableDefinition = appSchema.definitions[`${DefinitionName.ObjectPageTable}<${lineItemId}>`] as Definition;
    const creationModeProperty = tableDefinition?.properties?.creationMode as Definition;

    if (creationModeProperty?.$ref) {
        creationModeProperty.$ref = creationModeProperty.$ref.replace(definitionName, creationModeDefinitionName);
    }
}

/**
 * Find the relevant V4 page under the routing targets of manifest,json.
 *
 * @param pages - list of all pages in manifest
 * @param templateName - search criterion: template name
 * @param entitySet - search criterion: entity set object from AVT
 * @param contextPath - search criterion: contextPath
 * @returns the page definition in manifest (if found)
 */
export function findPageV4(
    pages: v4.SapUiAppPageV4[],
    templateName: string,
    entitySet?: EntitySet,
    contextPath?: string
): v4.SapUiAppPageV4 | undefined {
    let v4Page: v4.SapUiAppPageV4;

    for (const i in pages) {
        const pageSettings = pages[i].options?.settings || {};
        const isConnectionMatches =
            (contextPath && pageSettings.contextPath === contextPath) ||
            (entitySet?.name && pageSettings.entitySet === entitySet.name);
        if (
            isConnectionMatches &&
            (compareTemplateNames(templateName, pages[i].name) ||
                (templateName === v4.FE_TEMPLATE_V4_ALP && pages[i].name === v4.FE_TEMPLATE_V4_LIST_REPORT))
        ) {
            v4Page = pages[i];
            break;
        }
    }
    return v4Page;
}

/**
 * Depending on the template type, strip down the app schema so that only the relevant views are part of it.
 *
 * @param {Definition} appSchema - app specific JSON schema, to be adjusted
 * @param templateType - template type of the current page
 * @returns the right definition name for the table in the schema
 */
export function alignSchemaWithTemplateType(
    appSchema: Definition,
    templateType: TemplateType
): DefinitionName.Table | DefinitionName.ALPTableView {
    let tableDefinitionName;
    const tableDefinition = appSchema.properties[PropertyName.table] as Definition;
    if (templateType === TemplateType.AnalyticalListPageV4) {
        tableDefinitionName = DefinitionName.ALPTableView;

        delete tableDefinition.anyOf;
        tableDefinition.$ref = `${DEFINITION_LINK_PREFIX}${DefinitionName.ALPTableView}`;
        delete appSchema.definitions[DefinitionName.Table];
        delete appSchema.definitions[DefinitionName.LRChartView];
        delete appSchema.definitions[DefinitionName.LRTableView];
        delete appSchema.definitions[DefinitionName.MultiTableModeV4];
    } else if (templateType === TemplateType.ListReportObjectPageV4) {
        tableDefinitionName = DefinitionName.Table;

        delete tableDefinition.anyOf;
        tableDefinition.$ref = `${DEFINITION_LINK_PREFIX}${DefinitionName.Table}`;
        (appSchema.definitions[tableDefinitionName] as Definition).properties[PropertyName.annotationPath][
            SchemaTag.hidden
        ] = true;
        delete appSchema.definitions[DefinitionName.ALPTableView];
        appSchema.properties[PropertyName.defaultPath][SchemaTag.hidden] = true;
    }
    return tableDefinitionName;
}

/**
 * Common function for enhancing LineItems, FieldGroups and Custom Section definitions of app schema by custom action definitions.
 *
 * @param appSchema The application schema where the custom action definition will be added.
 * @param v4Page The V4 page configuration object that contains metadata and settings for the page.
 * @param logger A logging instance for logging warnings or errors during the processing.
 * @param customActionDefinitionName The name of the custom action definition to be added. Defaults to `DefinitionName.CustomTableAction`.
 * @param sectionDefinitionName The name of the section definition within the schema to which the action definition will be added.
 * @param sectionIdInManifest Optional: The section ID in the manifest, used to derive the configuration ID.
 * @param lineItemId Optional: The ID of the line item, if applicable.
 */
export function addCustomActionDefinition(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    logger: ExtensionLogger,
    customActionDefinitionName: string = DefinitionName.CustomTableAction,
    sectionDefinitionName = '',
    sectionIdInManifest = '',
    lineItemId?: string
): void {
    const definition = appSchema.definitions[sectionDefinitionName];
    if (!definition) {
        return;
    }
    // Sort existing actions
    let sortedActionsArray = sortPropertiesRecords(definition);

    let configId;
    if (sectionIdInManifest) {
        if (customActionDefinitionName.indexOf(DefinitionName.CustomFormActionOP) !== -1) {
            configId = sectionIdInManifest.replace(/::/g, '#');
        } else {
            configId = sectionIdInManifest.replace(/::/g, '/');
        }
    } else {
        if (customActionDefinitionName === DefinitionName.CustomFormActionOP) {
            configId = `@${UIAnnotationTerms.FieldGroup}`;
        } else {
            configId = `@${UIAnnotationTerms.LineItem}`;
        }
    }
    let actions;
    if (sectionDefinitionName.includes(DefinitionName.ObjectPageCustomSectionActions)) {
        actions = v4Page.options?.settings?.content?.body?.sections?.[configId]?.['actions'];
    } else {
        actions = v4Page.options?.settings?.controlConfiguration?.[configId]?.['actions'];
    }

    if (actions) {
        // Add custom actions and custom action menus to the actions array
        const parameters: ActionProcessingParameters = {
            extensions: actions,
            appSchema,
            customActionDefinitionName,
            initialActionDetailsList: sortedActionsArray as ActionDetails[],
            logger,
            lineItemId
        };
        sortedActionsArray = addActionsToArray(parameters);
    }

    // Adjust propertyIndex
    if (sortedActionsArray) {
        for (let index = 0; index < sortedActionsArray.length; index++) {
            sortedActionsArray[index][1][SchemaTag.propertyIndex] = index;
        }
        const sortedActionsAsObject = {};
        sortedActionsArray.forEach(([key, value]) => (sortedActionsAsObject[key] = value));
        (appSchema.definitions[sectionDefinitionName] as Definition).properties = sortedActionsAsObject;
    }
}

/**
 * Common function for enhancing header and footer definitions of app schema by custom action definitions.
 *
 * @param appSchema - app-specific JSON schema
 * @param v4Page - actual page in the manifest
 * @param logger - logger for error messages
 * @param customActionDefinitionName - definition name of custom action
 * @param sectionId - identifier of the current object page section in schema
 */
export function addHeaderFooterCustomActionDefinition(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    logger: ExtensionLogger,
    customActionDefinitionName: DefinitionName = DefinitionName.CustomHeaderActionOP,
    sectionId = ''
): void {
    const definition = appSchema.definitions[sectionId];
    if (!definition) {
        return;
    }
    // Sort existing actions
    let sortedActionsArray = sortPropertiesRecords(definition);
    let actions;
    if ([DefinitionName.CustomHeaderAction, DefinitionName.CustomHeaderActionOP].includes(customActionDefinitionName)) {
        actions = v4Page.options?.settings?.content?.header?.['actions'];
    } else {
        actions = v4Page.options?.settings?.content?.footer?.['actions'];
    }
    if (actions) {
        // Add custom actions and custom action menus to the actions array
        const parameters: ActionProcessingParameters = {
            extensions: actions,
            appSchema,
            customActionDefinitionName,
            initialActionDetailsList: sortedActionsArray as ActionDetails[],
            logger
        };
        sortedActionsArray = addActionsToArray(parameters);
    }
    // Adjust propertyIndex
    const sortedActionsAsObject = {};
    if (sortedActionsArray) {
        for (let index = 0; index < sortedActionsArray.length; index++) {
            sortedActionsArray[index][1][SchemaTag.propertyIndex] = index;
        }
        sortedActionsArray.forEach(([key, value]) => (sortedActionsAsObject[key] = value));
    }
    (appSchema.definitions[sectionId] as Definition).properties = sortedActionsAsObject;
}

/**
 * Adds actions and custom action menus to an array of initial actions using specified parameters.
 *
 * @param params - An object containing the parameters for adding actions to the array:
 *   - actions: The original actions to be processed.
 *   - initialActionsArray: The array to which actions should be added.
 *   - customActionDefinitionName: The name of the custom action definition.
 *   - appSchema: The application schema used for processing.
 *   - logger: An object used for logging information during processing.
 * @returns An array of action details after processing and adding extensions.
 */
function addActionsToArray(params: ActionProcessingParameters): ActionDetails[] {
    const { extensions, initialActionDetailsList, customActionDefinitionName, logger } = params;

    // Add custom actions
    let actionDetailsList = addExtensionToArray(
        extensions,
        initialActionDetailsList,
        customActionDefinitionName,
        [TemplatePropertyName.Press, TemplatePropertyName.Menu],
        logger,
        false,
        params.appSchema
    );
    const processActionMenusParams: ActionProcessingParameters = {
        ...params,
        initialActionDetailsList: actionDetailsList
    };

    actionDetailsList = processActionMenus(processActionMenusParams);
    return actionDetailsList;
}

/**
 * Processes action menus by identifying menu-type actions within the provided extensions and updating the list of action details accordingly.
 *
 * @param {ActionProcessingParameters} params - An object containing required parameters for processing action menus. It includes:
 *   - extensions: A map of extension data where the keys are extension IDs and the values are extension definitions.
 *   - initialActionDetailsList: A list of action details, each represented as a tuple with the action ID and action definition.
 * @returns {Array} An updated list of action details after processing any menu-type actions.
 */
function processActionMenus(params: ActionProcessingParameters) {
    const { extensions, initialActionDetailsList } = params;
    let actionDetailsList = [...initialActionDetailsList];

    // Currently, all actions were initialized as custom actions. We need to check if the action is a menu and if so,
    // we need to process it differently.
    for (const extensionId in extensions) {
        const extension = extensions[extensionId];
        const actionDetails = actionDetailsList.find(([actionId]) => actionId === extensionId);
        if (Object.keys(extension).includes(PropertyName.menu) && actionDetails) {
            const [, actionDefinition] = actionDetails;
            actionDetailsList = processActionMenu(
                params,
                extension,
                actionDefinition as SchemaDefinition,
                actionDetailsList,
                extensionId
            );
        }
    }
    return actionDetailsList;
}

/**
 * Retrieves a reference to a menu entry definition based on the provided parameters.
 *
 * @param {string} $ref - The reference string used to identify the menu entry definition.
 * @param {string} actionMenuDefinitionName - The name of the action menu definition.
 * @param {Definition} appSchema - The application schema containing the definitions.
 * @returns {string} The constructed link or reference for the specified menu entry definition.
 */
function getMenuEntryDefinitionLink($ref: string, actionMenuDefinitionName: string, appSchema: Definition): string {
    const definitionName = getDefinitionKey($ref);
    const { baseDefinitionName, definitionQualifier } = parseDefinitionName(definitionName);

    // incorrect definition name - probably a reference to an intent based navigation action
    if (baseDefinitionName.includes(FIELD_SEPARATOR)) {
        return $ref;
    }
    const menuEntryDefinitionQualifier = definitionQualifier
        ? `${actionMenuDefinitionName}${FIELD_SEPARATOR}${definitionQualifier}`
        : actionMenuDefinitionName;
    const menuEntryDefinitionRef = getFacetDefinitionKey(baseDefinitionName, menuEntryDefinitionQualifier);
    const menuEntryDefinitionLink = getFacetDefinitionLink(baseDefinitionName, menuEntryDefinitionQualifier);
    const menuEntryDefinition = appSchema.definitions[menuEntryDefinitionRef];
    if (!menuEntryDefinition) {
        const originalDefinition = appSchema.definitions[definitionName];
        const menuEntryDefinition = parseSchemaDefinition(baseDefinitionName, menuEntryDefinitionQualifier, appSchema);
        for (const originalDefinitionKey of Object.keys(originalDefinition)) {
            if (menuEntryDefinition[originalDefinitionKey] === undefined) {
                menuEntryDefinition[originalDefinitionKey] = originalDefinition[originalDefinitionKey];
            }
        }
        // some properties doesn't make sense for menu entries - hide them instead of deleting
        // NOTE: This is a necessary trade-off. When moving IBN actions in and out of menus,
        // we keep these properties visible in the manifest but hidden in the UI.
        // Unfortunately, this is required - otherwise, when renaming actions, we end up with
        // orphaned entries in the manifest.
        if (menuEntryDefinition.properties[PropertyName.position]) {
            menuEntryDefinition.properties[PropertyName.position][SchemaTag.hidden] = true;
        }
        if (menuEntryDefinition.properties[PropertyName.overflowGroup]) {
            menuEntryDefinition.properties[PropertyName.overflowGroup][SchemaTag.hidden] = true;
        }
        if (menuEntryDefinition.properties[PropertyName.priority]) {
            menuEntryDefinition.properties[PropertyName.priority][SchemaTag.hidden] = true;
        }
    }

    return menuEntryDefinitionLink;
}

/**
 * Determines the appropriate action definition names based on a custom action definition name.
 * Returns menu definition names and the action name for menu entries.
 *
 * @param {string} customActionDefinitionName - The custom action definition name to be parsed and matched.
 * @returns {ActionMenuDefinitionName} The corresponding action definition names, or a default value if not found.
 */
function determineActionDefinitionNames(customActionDefinitionName: string): ActionMenuDefinitionName {
    const { baseDefinitionName } = parseDefinitionName(customActionDefinitionName);
    return actionMenuDefinitionNameMap.get(baseDefinitionName) ?? LR_ACTION_MENU_DEFINITION;
}

/**
 * Parses a given definition name into its base name and optional qualifier.
 *
 * @param definitionName The definition name to parse. This should be a string in the format "BaseName" or "BaseName<Qualifier>".
 * @returns An object containing the base definition name and, if applicable, its qualifier.
 * If there is no qualifier, it will return undefined for the qualifier field.
 */
export function parseDefinitionName(definitionName = ''): ParsedDefinitionName {
    const match = /^([^<]*)(?:<(.*)>)?$/.exec(definitionName);

    if (!match) {
        return { baseDefinitionName: definitionName as DefinitionName };
    }

    const [, baseDefinitionName, definitionQualifier] = match;
    return {
        baseDefinitionName: baseDefinitionName as DefinitionName,
        definitionQualifier: definitionQualifier ?? undefined
    };
}

/**
 * Updates the custom menu's default action to include a list of valid options based on the provided menu schema.
 *
 * @param {Definition} customMenuDefinition - The definition object for the custom menu, where the default action will be updated.
 * @param {Record<string, SchemaDefinition>} [menu] - A mapping of menu items to their schema definitions, used to generate valid options for the default action.
 */
function setCustomMenuDefaultAction(customMenuDefinition: Definition, menu: Record<string, SchemaDefinition> = {}) {
    if (customMenuDefinition.properties[PropertyName.defaultAction]) {
        const menuOneOfValues: OneOfOption[] = Object.keys(menu).map((value) => ({
            const: value,
            description: menu[value].description
        }));
        customMenuDefinition.properties[PropertyName.defaultAction][PropertyName.oneOf] = menuOneOfValues;
    }
}

/**
 * Retrieves the description of a menu entry from its schema definition.
 *
 * @param menuEntryDefinition The schema definition of the menu entry, including its reference information.
 * @param appSchema The complete application schema containing definitions for the menu entries.
 * @returns The description of the menu entry if found, or an empty string if no description is available.
 */
function getMenuEntryDescription(menuEntryDefinition: SchemaDefinition, appSchema: Definition) {
    const definitionKey = getDefinitionKey(menuEntryDefinition.$ref);
    const definition = appSchema.definitions[definitionKey] as Definition;
    return menuEntryDefinition?.description ?? definition?.description ?? '';
}

/**
 * Processes an action menu for a custom extension, updating the schema definition and action details list accordingly.
 *
 * @param {ActionProcessingParameters} params The parameters required for action menu processing, including `actionMenu`, `actionMenuActions`, `appSchema`, and `lineItemId`.
 * @param {CustomExtension} extension The custom extension that contains the action menu configuration.
 * @param {SchemaDefinition} actionDefinition The schema definition object to be populated with details of the custom action menu.
 * @param {ActionDetails[]} initialDetailsList The initial list of action details to be processed and modified.
 * @param {string} extensionId The unique identifier for the extension being processed.
 * @returns {ActionDetails[] | undefined} Returns the updated list of action details if processing occurs; otherwise, returns `undefined` if the extension is not a custom action menu.
 */
function processActionMenu(
    params: ActionProcessingParameters,
    extension: CustomExtension,
    actionDefinition: SchemaDefinition,
    initialDetailsList: ActionDetails[],
    extensionId: string
): ActionDetails[] | undefined {
    // if the extension isn't a custom action menu, don't do anything
    if (!(TemplatePropertyName.Menu in extension)) {
        return undefined;
    }

    const { customActionDefinitionName, appSchema, lineItemId } = params;
    const detailsList = [...initialDetailsList];
    const { actionMenuDefinitionName, actionMenuActionsDefinitionName } =
        determineActionDefinitionNames(customActionDefinitionName);

    // Prepare submenu entries to be processed
    const menu = extension[TemplatePropertyName.Menu] ?? [];
    const properties: Record<string, SchemaDefinition> = {};

    // For forms and tables, the id should contain both the section and action names
    const extendedExtensionId = lineItemId ? `${extensionId}${FIELD_SEPARATOR}${lineItemId}` : extensionId;

    // Create the specific action menu definition in the schema based on the generic action menu definition
    const customMenuDefinition = parseSchemaDefinition(actionMenuDefinitionName, extendedExtensionId, appSchema);
    actionDefinition.$ref = getFacetDefinitionLink(actionMenuDefinitionName, extendedExtensionId);

    actionDefinition[SchemaTag.actionType] = ActionType.CustomMenu;

    const customMenuActionsDefinition = parseSchemaDefinition(
        actionMenuActionsDefinitionName,
        extendedExtensionId,
        appSchema
    );

    const customMenuActionsDefinitionLink = getFacetDefinitionLink(
        actionMenuActionsDefinitionName,
        extendedExtensionId
    );

    // for manifest based menu actions, we need to store the menu entries from the manifest
    actionDefinition[PropertyName.menu] = menu;

    // Add the menu items to the action menu definition
    menu.forEach((menuEntry, propertyIndex) => {
        const sortedExtensionIndex = detailsList.findIndex(([entry]) => entry === menuEntry);

        // If the list of actions contains the menu entry, add the menu entry to the properties of the custom action menu
        // and remove the extension reference from the list of actions
        if (sortedExtensionIndex !== -1) {
            const menuEntryDefinition = detailsList[sortedExtensionIndex][1] as SchemaDefinition;
            menuEntryDefinition.propertyIndex = propertyIndex;
            menuEntryDefinition.isViewNode = true;
            menuEntryDefinition.$ref = getMenuEntryDefinitionLink(
                menuEntryDefinition.$ref,
                actionMenuDefinitionName,
                appSchema
            );
            menuEntryDefinition.description = getMenuEntryDescription(menuEntryDefinition, appSchema);

            properties[menuEntry] = menuEntryDefinition;
            detailsList.splice(sortedExtensionIndex, 1);
        }
    });

    customMenuDefinition.description = actionDefinition.description;
    // Update the properties of the custom action menu definition with the menu items
    const actions = customMenuDefinition.properties[PropertyName.actions] as Definition;

    setCustomMenuDefaultAction(customMenuDefinition, properties);

    customMenuActionsDefinition.properties = properties;

    actions.$ref = customMenuActionsDefinitionLink;
    return detailsList;
}

/**
 * Method returns page from manifest by passed page key.
 *
 * @param manifest Object from manifest.json.
 * @param pageId Page id.
 * @param logger Logger to report error of unexisting page.
 * @returns Manifest's target page.
 */
export function getManifestPage(
    manifest: Manifest,
    pageId: string,
    logger?: ExtensionLogger
): v4.RoutingTargetOptions | undefined {
    const pages = getJSONPropertyByPath(manifest, [
        ManifestSection.ui5,
        'routing',
        'targets'
    ]) as v4.RoutingTargetOptions;
    if (!pages) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOTARGETS'),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5, 'routing']
            }
        });
        return;
    }
    const page = getJSONPropertyByPath(pages, [pageId]) as v4.RoutingTargetOptions | undefined;
    if (!page) {
        log(logger, {
            severity: LogSeverity.Error,
            message: i18next.t('NOROUTINGID', { routingId: pageId }),
            location: {
                path: MANIFESTPATH,
                range: [ManifestSection.ui5, 'routing']
            }
        });
        return;
    }
    return page;
}

/**
 * Method finds custom view/section XML file for passed page.
 *
 * @param files All extension files.
 * @param page - the page object containing routing target options
 * @param manifest Object from manifest.json.
 * @param xmlType XML file type - view or fragment.
 * @param extensionName Extension namespace or path to extension name in page object.
 * @returns Custom page's data containing XML file.
 */
export function getPageCustomExtensionFile(
    files: FileData[],
    page: v4.RoutingTargetOptions,
    manifest: Manifest,
    xmlType: 'view' | 'fragment',
    extensionName: string
): ExtensionFileData | undefined {
    if (extensionName) {
        const appId = manifest['sap.app']?.id || '';
        const fileExtension = 'xml';
        if (extensionName.startsWith(appId)) {
            // Remove app id from extension name
            extensionName = extensionName.replace(appId, '');
        }
        const fileParts = extensionName.split('.');
        let baseName = fileParts.pop();
        baseName = `${baseName}.${xmlType}.${fileExtension}`;
        const extPath = join(...fileParts, baseName);
        const file = (files ?? []).find((file: FileData) => file.dataSourceUri.endsWith(extPath));
        if (file) {
            const settings = page.options?.settings as { contextPath?: string; entitySet?: string };
            return {
                file,
                relativeFilePath: extPath,
                connection: {
                    contextPath: settings?.contextPath,
                    entitySet: settings?.entitySet
                }
            };
        }
    }
    return undefined;
}

/**
 * Method finds view XML file for passed page.
 *
 * @param files All view files.
 * @param pageId Page id.
 * @param manifest Object from manifest.json.
 * @param logger Logger.
 * @returns Custom page's data containing view XML file.
 */
export function getPageCustomViewFile(
    files: FileData[],
    pageId: string,
    manifest: Manifest,
    logger?: ExtensionLogger
): ExtensionFileData | undefined {
    const page = getManifestPage(manifest, pageId, logger);
    const viewName = getJSONPropertyByPath(page, ['options', 'settings', 'viewName']);
    return page && typeof viewName === 'string'
        ? getPageCustomExtensionFile(files, page, manifest, 'view', viewName)
        : undefined;
}

/**
 * Adds custom section or custom sub section definitions to app schema.
 *
 * @param appSchema - app-specific JSON schema
 * @param definition - sections definition
 * @param v4Page - actual page in the manifest
 * @param customSectionRef - value of custom section ref
 * @param isMergedSections - are merged section used
 * @param facetKey - facet key
 * @param logger - logger for error messages
 */
export function addCustomSectionDefinition(
    appSchema: Definition,
    definition: Definition,
    v4Page: v4.SapUiAppPageV4,
    customSectionRef: string,
    isMergedSections: boolean,
    facetKey?: string,
    logger?: ExtensionLogger
): void {
    let sortedSectionsArray = sortPropertiesRecords(definition);
    const annotationSectionsMap = new Map<string, string>();
    sortedSectionsArray = sortedSectionsArray.map((annotationSection) => {
        const entry = getEnumEntry(appSchema, annotationSection[1] as SchemaDefinition, annotationSection[0], {
            keyProperties: [SchemaKeyName.id],
            descriptionAsFallback: true,
            resolveDefinition: true
        });
        annotationSectionsMap.set(entry.const, annotationSection[0]);
        return [entry.const, annotationSection[1]];
    });
    if (facetKey) {
        // check for already defined section
        const section = v4Page.options?.settings?.content?.body?.sections?.[facetKey];
        if (section && 'subSections' in section) {
            // add extension to subsection of section
            sortedSectionsArray = addExtensionToArray(
                section.subSections,
                sortedSectionsArray,
                customSectionRef,
                TemplatePropertyName.Template,
                logger,
                isMergedSections
            );
        }
    } else if (customSectionRef.includes(DefinitionName.ObjectPageCustomHeaderSectionFragment)) {
        // header custom section extensions
        const header = v4Page.options?.settings?.content?.header;
        if (header && 'facets' in header) {
            // add extension to header section
            sortedSectionsArray = addExtensionToArray(
                header.facets as CustomExtensions,
                sortedSectionsArray,
                customSectionRef,
                TemplatePropertyName.Template,
                logger,
                isMergedSections
            );
        }
    } else {
        // body custom section extensions
        const sections = v4Page.options?.settings?.content?.body?.sections;
        if (sections) {
            // add extension to sections section
            sortedSectionsArray = addExtensionToArray(
                sections as CustomExtensions,
                sortedSectionsArray,
                customSectionRef,
                TemplatePropertyName.Template,
                logger,
                isMergedSections
            );
        }
    }
    // Adjust propertyIndex
    for (let index = 0; index < sortedSectionsArray.length; index++) {
        sortedSectionsArray[index][1][SchemaTag.propertyIndex] = index;
    }
    const sortedActionsAsObject = {};
    sortedSectionsArray.forEach(([key, value]) => {
        key = annotationSectionsMap.get(key) || key;
        sortedActionsAsObject[key] = value;
    });
    definition.properties = sortedActionsAsObject;
}

/**
 * Method adds custom filter fields from manifest to schema properties.
 *
 * @param appSchema - app-specific JSON schema
 * @param definition - filter fields definition
 * @param v4Page - actual page in the manifest
 */
export function addCustomFilterFieldDefinition(
    appSchema: Definition,
    definition: Definition,
    v4Page: v4.SapUiAppPageV4
): void {
    let sortedFilterFieldsArray: [string, DefinitionOrBoolean][] = sortPropertiesRecords(definition);
    const annotationSectionsMap = new Map<string, string>();
    sortedFilterFieldsArray = sortedFilterFieldsArray.map((annotationSection) => {
        const entry = getEnumEntry(appSchema, annotationSection[1] as SchemaDefinition, annotationSection[0], {
            keyProperties: [SchemaKeyName.value],
            descriptionAsFallback: true,
            resolveDefinition: true
        });
        annotationSectionsMap.set(entry.const, annotationSection[0]);
        return [entry.const, annotationSection[1]];
    });
    // check for already defined section
    const selectionFieldsEntry =
        v4Page.options?.settings?.controlConfiguration?.[`@${UIAnnotationTerms.SelectionFields}`];
    if (typeof selectionFieldsEntry === 'object' && 'filterFields' in selectionFieldsEntry) {
        // add extension to subsection of section
        sortedFilterFieldsArray = addExtensionToArray(
            selectionFieldsEntry.filterFields as CustomExtensions,
            sortedFilterFieldsArray,
            DefinitionName.CustomFilterField,
            TemplatePropertyName.Template
        );
    }
    // Adjust propertyIndex
    for (let index = 0; index < sortedFilterFieldsArray.length; index++) {
        sortedFilterFieldsArray[index][1][SchemaTag.propertyIndex] = index;
    }
    const sortedFilterFieldsAsObject = {};
    sortedFilterFieldsArray.forEach(([key, value]) => {
        key = annotationSectionsMap.get(key) || key;
        sortedFilterFieldsAsObject[key] = value;
    });
    definition.properties = sortedFilterFieldsAsObject;
}

/**
 * Imports action settings into a configuration object and applies necessary transformations based on provided parameters.
 *
 * @param {object} importActionParams - Parameters required for importing action settings, including schema, factory, and action specifications.
 * @param {string} key - The key used to identify the specific configuration or schema definition.
 * @param {boolean} [keepEmptyAction] - Determines whether to keep empty actions in the configuration object.
 */
function importActionToConfigurationObject(
    importActionParams: ImportActionSettingsOfReferenceParams,
    key: string,
    keepEmptyAction = false
) {
    const { appSchema, factory, actionsInConfig, actionKey, manifest, routingId, manifestSectionId, targetAnnotation } =
        importActionParams;
    const schemaPropertyName = (key ?? '').split('<')[0];
    const metaInstance = factory.createInstance(PageTypeV4.ObjectPage, schemaPropertyName);
    const breadcrumbs = [actionKey.replace('/', '??')];
    if (targetAnnotation) {
        breadcrumbs.push(targetAnnotation);
    }
    if (metaInstance) {
        const action = (actionsInConfig[actionKey] = metaInstance);
        importSettingsOfObject(action, manifest, appSchema.definitions[key], routingId, breadcrumbs, manifestSectionId);
        if (!keepEmptyAction) {
            removeEmptyStructure(actionsInConfig, actionKey);
        }
    }
}

/**
 * Imports the settings of a single action reference into the configuration object.
 *
 * @param {ImportActionSettingsOfReferenceParams} importActionParams - The parameters required for importing action settings, including property definitions.
 * @param {Definition} definitionOfReference - The reference definition object used to resolve the action.
 */
export function importSingleActionSettingsOfReference(
    importActionParams: ImportActionSettingsOfReferenceParams,
    definitionOfReference: Definition
) {
    const { actionPropertyDefinition } = importActionParams;
    const key = getDefinitionKey(definitionOfReference.$ref || actionPropertyDefinition.$ref);

    importActionToConfigurationObject(importActionParams, key);
}

/**
 * Imports and processes the settings for a group of actions based on a reference definition.
 *
 * @param {ImportActionSettingsOfReferenceParams} importActionParams - Parameters containing details required to import group action settings, such as action property definitions, application schema, action configurations, and action keys.
 * @param {Definition} definitionOfReference - The definition object that serves as the reference for importing group action settings.
 * @param {string} definitionKey - Optional, the reference definition key from which the action group settings are imported.
 */
export function importGroupActionSettingsOfReference(
    importActionParams: ImportActionSettingsOfReferenceParams,
    definitionOfReference: Definition,
    definitionKey?: string
) {
    const { actionPropertyDefinition, appSchema, actionsInConfig, actionKey } = importActionParams;
    let resolvedDefinitionOfReference = definitionOfReference;
    let resolvedDefinitionKey = getDefinitionKey(resolvedDefinitionOfReference.$ref ?? actionPropertyDefinition.$ref);

    /* Some action groups (e.g. form group actions) have wrapper definition in the schema, in such cases we need to use the referenced definition. */
    const hasActionGroupWrapper =
        getDefinitionKey(definitionOfReference.$ref) === `ObjectPageFormActionGroup<${definitionKey}>`;
    if (hasActionGroupWrapper) {
        resolvedDefinitionKey = getDefinitionKey(definitionOfReference.$ref);
        resolvedDefinitionOfReference = appSchema.definitions[resolvedDefinitionKey] as Definition;
    }

    // create an instance of the action group in the configuration object
    importActionToConfigurationObject(importActionParams, resolvedDefinitionKey, true);
    // newly created action group instance is available in the actionsInConfig object
    const actionGroup = actionsInConfig[actionKey];

    // fill the action group with the action settings of the group action
    const { properties } = resolvedDefinitionOfReference;
    for (const propertyKey in properties) {
        const actionInGroupProperty = properties[propertyKey];
        if (actionInGroupProperty['$ref']) {
            const actionInGroupPropertyKey = getDefinitionKey(actionInGroupProperty['$ref']);
            const actionInGroupDefinition = appSchema.definitions[actionInGroupPropertyKey] as Definition;
            const key = getDefinitionKey(actionInGroupDefinition.$ref ?? actionInGroupProperty['$ref']);
            const importGroupedActionParams: ImportActionSettingsOfReferenceParams = {
                ...importActionParams,
                actionsInConfig: actionGroup,
                actionKey: propertyKey
            };

            importActionToConfigurationObject(importGroupedActionParams, key);
        }
    }
    removeEmptyStructure(actionsInConfig, actionKey);
}

/**
 * Imports and updates the action menu settings in a configuration object based on a given reference definition and related action parameters.
 *
 * @param {ImportActionSettingsOfReferenceParams} importActionParams - An object containing details such as the action property definition, application schema, actions configuration, and action key. These parameters are used for resolving and importing the settings.
 * @param {Definition} definitionOfReference - The reference definition object which includes properties and other settings required to define action menus and their related configurations.
 */
export function importManifestBasedActionMenuSettingsOfReference(
    importActionParams: ImportActionSettingsOfReferenceParams,
    definitionOfReference: Definition
) {
    const { actionPropertyDefinition, appSchema, actionsInConfig, actionKey } = importActionParams;

    // create an instance of the menu action in the configuration object
    const key = getDefinitionKey(definitionOfReference.$ref ?? actionPropertyDefinition.$ref);
    importActionToConfigurationObject(importActionParams, key, true);

    // newly created menu action instance is available in the actionsInConfig object
    const menuAction = actionsInConfig[actionKey];
    menuAction[PropertyName.menu] = actionPropertyDefinition[PropertyName.menu];
    menuAction[PropertyName.actions] = {};
    menuAction[PropertyName.text] = definitionOfReference.description;

    const actionsDefinitionKey = getDefinitionKey(
        (definitionOfReference?.properties?.[PropertyName.actions] as Definition)?.$ref
    );
    const actionsDefinition = appSchema.definitions[actionsDefinitionKey] as Definition;
    // fill the action menu with the action settings of the menu action
    const actions = actionsDefinition?.properties ?? {};
    for (const action in actions) {
        const actionInMenuActionProperty = actions[action];
        const actionInMenuActionPropertyKey = getDefinitionKey(actionInMenuActionProperty['$ref']);
        const actionInMenuActionDefinition = appSchema.definitions[actionInMenuActionPropertyKey] as Definition;
        const key = getDefinitionKey(actionInMenuActionDefinition?.$ref ?? actionInMenuActionProperty['$ref']);
        const importMenuActionParams: ImportActionSettingsOfReferenceParams = {
            ...importActionParams,
            actionsInConfig: menuAction[PropertyName.actions],
            actionKey: action
        };

        importActionToConfigurationObject(importMenuActionParams, key);
    }

    removeEmptyStructure(actionsInConfig, actionKey);
}

/**
 * Determines the strategy type for action settings based on the provided data type and action type.
 *
 * @param {string} [dataType] - The data type associated with the action settings. Defaults to an empty string.
 * @param {string} [actionType] - The action type within the context of the action settings. Defaults to an empty string.
 * @returns {DataFieldStrategyTypes} The corresponding strategy type for the given data type and action type.
 */
function determineActionStrategyType(dataType: string = '', actionType: string = ''): DataFieldStrategyTypes {
    const key = `${dataType}${actionType}`;
    return strategyTypeMap.get(key) || UIAnnotationTypes.DataFieldForAction;
}

/**
 * Imports the action settings of a specified reference based on the given parameters.
 *
 * @param {object} importActionParams - The parameters required for importing action settings.
 * @param {object} importActionParams.actionPropertyDefinition - The action property definition containing the $ref to the reference.
 * @param {object} importActionParams.appSchema - The application schema containing definitions and other related metadata.
 */
export function importActionSettingsOfReference(importActionParams: ImportActionSettingsOfReferenceParams) {
    const { actionPropertyDefinition, appSchema } = importActionParams;
    const definitionKey = getDefinitionKey(actionPropertyDefinition.$ref);
    const definitionOfReference = appSchema.definitions[definitionKey] as Definition;
    const dataType = definitionOfReference?.['dataType'];
    const actionType = actionPropertyDefinition?.[ACTION_TYPE];
    const strategyType = determineActionStrategyType(dataType, actionType);

    const strategy = dataFieldStrategyContext.getStrategy(strategyType);
    strategy.importActionSettingsOfReference(importActionParams, definitionOfReference, definitionKey);
}

/**
 * Enhances the application schema with custom field definitions for Object Page sections.
 *
 * @param appSchema - The application-specific JSON schema object representing the Object Page.
 * @param v4Page - The current Object Page configuration from the manifest (V4 format).
 * @param fieldGroupInManifest - The manifest identifier for the Object Page section associated with the field definitions.
 * @param logger - Logger instance for reporting or debugging schema processing errors.
 * @param customFieldDefinitionName - The schema definition name to be used for custom fields (defaults to `CustomTableAction`).
 * @param fieldsDefinitionName - The schema definition name for the set of fields being enhanced.
 */
export function addCustomFieldDefinition(
    appSchema: Definition,
    v4Page: v4.SapUiAppPageV4,
    fieldGroupInManifest: string,
    logger: ExtensionLogger,
    customFieldDefinitionName: string,
    fieldsDefinitionName: string
): void {
    const definition = appSchema.definitions[fieldsDefinitionName];
    if (!definition) {
        return;
    }
    // Sort existing actions
    let sortedFieldsArray = sortPropertiesRecords(definition);
    const configId = fieldGroupInManifest.replace(/::/g, '#');
    const fields = v4Page.options?.settings?.controlConfiguration?.[configId]?.['fields'];
    if (fields) {
        sortedFieldsArray = addExtensionToArray(
            fields,
            sortedFieldsArray,
            customFieldDefinitionName,
            TemplatePropertyName.Template,
            logger
        );
    }
    // Adjust propertyIndex
    if (sortedFieldsArray) {
        for (let index = 0; index < sortedFieldsArray.length; index++) {
            sortedFieldsArray[index][1][SchemaTag.propertyIndex] = index;
        }
        const sortedFieldsAsObject = {};
        sortedFieldsArray.forEach(([key, value]) => (sortedFieldsAsObject[key] = value));
        (appSchema.definitions[fieldsDefinitionName] as Definition).properties = sortedFieldsAsObject;
    }
}

/**
 * Adds 'manifestPath' to each property in a definition based on metadata sync rules.
 *
 * @param {string} appSchema - Full schema.
 * @param {string | undefined} definitionName - The name of the definition/class whose properties are being processed.
 *   If `undefined`, the function attempts to resolve the definition name from the `$ref` property of the provided definition.
 * @param {Definition} definition - The definition object containing property schemas.
 * @param {ManifestPathParams} pathParams - Additional context required to compute manifest paths.
 * @param handleRelative - A flag indicating whether to use relative paths for definition properties.
 */
export function addManifestPathsToProperties(
    appSchema: Definition,
    definitionName: string | undefined,
    definition: Definition,
    pathParams: ManifestPathParams,
    handleRelative = false
): void {
    addManifestPathsToPropertiesCommon(
        [v4controls, pages],
        appSchema,
        definitionName,
        definition,
        pathParams,
        undefined,
        handleRelative
    );
}

/**
 * Determines whether an entity set or navigation target is insertable based on its annotations.
 *
 * When navigationPath is provided:
 * 1. Checks NavigationRestrictions.RestrictedProperties on the source EntitySet
 * 2. Resolves the target EntitySet via navigationPropertyBinding
 * 3. Checks InsertRestrictions on the target EntitySet
 * 4. Both must allow insertion for the function to return true
 *
 * When navigationPath is not provided:
 * 1. Checks InsertRestrictions on the given EntitySet
 *
 * For Path-based (dynamic) Insertable values, always returns true (shows creationMode).
 *
 * @param entitySet - The source entity set (where navigation starts from, or the entity set being checked)
 * @param navigationPath - Optional navigation property path (e.g., "_Booking", "to_Items")
 * @returns A boolean indicating whether insertion is allowed. Returns true if the insertability is Path-based (dynamic) or undefined.
 */
export function getInsertableForEntitySet(entitySet: EntitySet, navigationPath?: string): boolean {
    // Check NavigationRestrictions if navigation path provided
    // Default: allow if not specified (Path-based values are also treated as true)
    let navigationInsertable = true;

    if (navigationPath) {
        const navRestrictions = entitySet?.annotations?.Capabilities?.NavigationRestrictions;

        if (navRestrictions?.RestrictedProperties) {
            const restriction = navRestrictions.RestrictedProperties.find(
                (prop) => prop.NavigationProperty?.value === navigationPath
            );

            if (restriction?.InsertRestrictions?.Insertable !== undefined) {
                const navInsertable = restriction.InsertRestrictions.Insertable;

                if (typeof navInsertable === 'boolean') {
                    navigationInsertable = navInsertable;
                }
            }
        }

        // Resolve target EntitySet via navigationPropertyBinding
        const targetEntitySet = entitySet?.navigationPropertyBinding?.[navigationPath];
        if (targetEntitySet && '_type' in targetEntitySet && targetEntitySet._type === 'EntitySet') {
            // Default: allow if not specified (Path-based values are also treated as true)
            let targetEntitySetInsertable = true;

            const targetRestriction = targetEntitySet.annotations?.Capabilities?.InsertRestrictions?.Insertable;
            if (typeof targetRestriction === 'boolean') {
                targetEntitySetInsertable = targetRestriction;
            }

            // Both Navigation and Target EntitySet must allow insertion
            return navigationInsertable && targetEntitySetInsertable;
        }

        // If no targetEntitySet found, only check navigation restrictions
        return navigationInsertable;
    }

    // No navigation path: check EntitySet-level InsertRestrictions
    // Default: allow if not specified (Path-based values are also treated as true)
    let entitySetInsertable = true;

    const entitySetRestriction = entitySet?.annotations?.Capabilities?.InsertRestrictions?.Insertable;
    if (typeof entitySetRestriction === 'boolean') {
        entitySetInsertable = entitySetRestriction;
    }

    return entitySetInsertable;
}

/**
 * Returns the manifest path for a given page based on the provided path parameters.
 *
 * @param {ManifestPathParams} pathParams - Parameters containing page information for path resolution.
 * @returns {string | undefined} The resolved manifest path as a string, or undefined if not applicable.
 */
export function getPageManifestPath(pathParams: ManifestPathParams): string | undefined {
    if (!Array.isArray(pathParams.pageName)) {
        const syncPath = getPathForPage([pathParams.pageName]);
        return convertSyncRulePathToJsonPath(syncPath);
    }
}
